/**
 * App — 容器组件：两段式体验（设计文档 §5）。
 *
 *   未登录            → LoginScreen
 *   已登录·无剧本      → OnboardingView（全屏引导聊天，Manus 式入口）
 *   已登录·有剧本      → WorkspaceShell（编辑/画布/素材/预览 + 右侧 agent 侧栏）
 *
 * 纯视图在 views/* 与 ui/*；本组件只做副作用编排（fetch、SSE、PreviewController
 * 生命周期、编辑器 autosave、素材映射的读写、图片生成）。
 */

import { useCallback, useEffect, useMemo, useRef, useState, type ReactElement } from "react";
import { canonicalAssetId, canonicalLooseAssetId } from "@lunaverse-ide/shared";
import {
  LunariaClient,
  type AccountUsage,
  type AgentEvent,
  type AgentRunContext,
  type Capabilities,
  type StyleMeta,
  type ProjectMeta,
} from "./api/client.js";
import { useLocale, translate, type Entry, type Locale } from "./i18n/context.js";
import { COMMON } from "./i18n/common.js";
import { LoginScreen } from "./components/LoginScreen.js";
import { OnboardingView } from "./views/onboarding/OnboardingView.js";
import { WorkspaceShell, type WorkspaceView } from "./views/workspace/index.js";
import { CowritingPane, type CowritingHandle } from "./cowriting/CowritingPane.js";
import { StudioView } from "./views/studio/index.js";
import { AgentSidebar, type AgentContextChip, type AgentResultIssue } from "./views/agent/AgentSidebar.js";
import type {
  AgentConfirmationBrief,
  AgentConfirmationContextItem,
  AgentConfirmationKind,
  AgentConfirmationAnswer,
} from "./views/agent/AgentConfirmation.js";
import { describeBlock, EditorView, type EditorMode } from "./views/editor/EditorView.js";
import { diagnosticLineFromMessage, type CodeDiagnostic } from "./views/editor/CodeEditor.js";
import { CanvasView, type CanvasValidationState } from "./views/canvas/CanvasView.js";
import { AssetsView } from "./views/assets/index.js";
import { PreviewView } from "./views/preview/PreviewView.js";
import { Spinner, Dialog, Button } from "./ui/index.js";
import { parseSceneSymbols } from "./assets/symbols.js";
import { parseLsBlocks } from "./ls/parse-blocks.js";
import { type AssetMapping, type AssetKind, emptyMapping, bindSymbol, unbindSymbol } from "./assets/mapping.js";
import { classifyAsset } from "./assets/classify.js";
import { insertCanvasBlockNearLineWithResult } from "./views/editor/card-ops.js";
import { type InsertKind } from "./views/editor/block-templates.js";
import { initialAppState, reduceAgentEvent, startUserTurn, type AppState } from "./app-state.js";
import { PreviewController, type PlayerDecision, type StageState, type PreviewPassageTarget } from "./preview-state.js";

const IMAGE_ASSET_RE = /\.(png|jpe?g|webp|gif|svg)$/i;
/** debounce window for editor autosave (design §5.2 `--beat-autosave` 800ms) */
const AUTOSAVE_MS = 800;
const AGENT_SOURCE_CONTEXT_MAX = 6500;
const AGENT_SELECTED_CONTEXT_RADIUS = 7;
type PreviewInvalidReason = "source" | "assets";

export interface SceneSnapshot {
  readonly id: string;
  readonly source: string;
}

interface AgentSceneChanges {
  readonly createdSceneIds: readonly string[];
  readonly changedSceneIds: readonly string[];
}

type SceneSourceReader = Pick<LunariaClient, "readScene">;
type SceneSourceWriter = Pick<LunariaClient, "writeScene">;

export interface InitialWorkspaceDraftScene {
  readonly id: string;
  readonly source: string;
}

function initialWorkspaceDraftSource(locale: Locale): string {
  if (locale === "en") {
    return [
      '@episode main:01 "New Story" {',
      "  NARRATOR: (The first episode will be written after you confirm the direction. You can also edit here.)",
      "  @gate { @end to_be_continued }",
      "}",
    ].join("\n");
  }
  return [
    '@episode main:01 "新故事" {',
    "  NARRATOR: （AI 会在你确认方向后写入首集。你也可以直接在这里改。）",
    "  @gate { @end to_be_continued }",
    "}",
  ].join("\n");
}

export async function createInitialWorkspaceDraftScene(
  writer: SceneSourceWriter,
  projectId: string,
  locale: Locale,
): Promise<InitialWorkspaceDraftScene> {
  const draft = { id: "ep1", source: initialWorkspaceDraftSource(locale) };
  await writer.writeScene(projectId, draft.id, draft.source);
  return draft;
}

interface AgentUndoState {
  readonly snapshots: readonly SceneSnapshot[];
  readonly createdSceneIds: readonly string[];
  readonly activeEpisodeId: string | null;
}

interface AgentResultState {
  readonly changedSceneIds: readonly string[];
  readonly targetSceneId: string | null;
  readonly validationChecked: boolean;
  readonly validationIssues: readonly AgentResultIssue[];
}

interface PendingAgentRun {
  readonly prompt: string;
  readonly brief: AgentConfirmationBrief;
  readonly instruction?: string;
  readonly displayPrompt?: string;
  readonly mode?: "create" | "adapt";
  readonly source: "local" | "agent";
}

interface PendingAdaptRun {
  readonly novelText: string;
  readonly fileName?: string;
  readonly brief: AgentConfirmationBrief;
  readonly confirmationInstruction?: string;
  readonly source: "local" | "agent";
}

function uniqueSceneIds(ids: readonly string[]): string[] {
  return Array.from(new Set(ids.filter((id) => id.trim().length > 0)));
}

export async function resolveAgentSceneChanges(
  reader: SceneSourceReader,
  projectId: string,
  snapshots: readonly SceneSnapshot[],
  sceneIdsAfterRun: readonly string[],
  writtenSceneIds: readonly string[],
): Promise<AgentSceneChanges> {
  const beforeById = new Map(snapshots.map((snapshot) => [snapshot.id, snapshot.source]));
  const sceneIdSet = new Set(sceneIdsAfterRun);
  const createdSceneIds = sceneIdsAfterRun.filter((id) => !beforeById.has(id));
  const candidates = uniqueSceneIds([...writtenSceneIds, ...createdSceneIds]).filter((id) => sceneIdSet.has(id));
  const changedSceneIds: string[] = [];

  for (const sceneId of candidates) {
    const before = beforeById.get(sceneId);
    if (before === undefined) {
      changedSceneIds.push(sceneId);
      continue;
    }
    const after = await reader.readScene(projectId, sceneId);
    if (after !== before) changedSceneIds.push(sceneId);
  }

  return { createdSceneIds, changedSceneIds };
}

type SceneCompileReader = Pick<LunariaClient, "compileScene">;

export interface AgentPostRunValidation {
  readonly checked: boolean;
  readonly issues: readonly AgentResultIssue[];
}

export async function validateAgentChangedScenes(
  reader: SceneCompileReader,
  projectId: string,
  sceneIds: readonly string[],
  compilerAvailable: boolean,
): Promise<AgentPostRunValidation> {
  const uniqueIds = uniqueSceneIds(sceneIds);
  if (!compilerAvailable || uniqueIds.length === 0) return { checked: false, issues: [] };

  const issues: AgentResultIssue[] = [];
  for (const sceneId of uniqueIds) {
    try {
      const result = await reader.compileScene(projectId, sceneId);
      if (!result.ok) {
        const diagnostics = result.diagnostics.map((d) => d.message).filter((d) => d.trim().length > 0);
        issues.push({
          sceneId,
          diagnostics: diagnostics.length > 0 ? diagnostics : ["Compile failed without diagnostics."],
        });
      }
    } catch (err) {
      issues.push({ sceneId, diagnostics: [messageOf(err)] });
    }
  }

  return { checked: true, issues };
}

export function isAgentUndoShortcutKey(event: Pick<KeyboardEvent, "key" | "metaKey" | "ctrlKey" | "shiftKey" | "altKey">): boolean {
  return event.key.toLowerCase() === "z" && (event.metaKey || event.ctrlKey) && !event.shiftKey && !event.altKey;
}

function isEditableEventTarget(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return false;
  if (target instanceof HTMLElement && target.isContentEditable) return true;
  return Boolean(target.closest("input, textarea, select, [role='textbox']"));
}

function isCanvasEventTarget(target: EventTarget | null): boolean {
  const targetElement = target instanceof Element ? target : null;
  if (targetElement?.closest(".lc-canvas-studio")) return true;
  const activeElement = typeof document !== "undefined" && document.activeElement instanceof Element ? document.activeElement : null;
  return Boolean(activeElement?.closest(".lc-canvas-studio"));
}

function isBindableUpload(assetId: string, kind: AssetKind): boolean {
  const media = classifyAsset(assetId);
  return kind === "music" || kind === "sfx" ? media === "audio" : media === "image";
}

function readToken(): string {
  try {
    return window.localStorage.getItem("lunaria_token") ?? "";
  } catch {
    return "";
  }
}

function messageOf(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

function reportError(set: (fn: (s: AppState) => AppState) => void, err: unknown): void {
  set((s) => reduceAgentEvent(s, { type: "error", error: { kind: "unknown", message: messageOf(err) } }));
}

function saveBlobToDisk(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function numberedSourceExcerpt(source: string): string | undefined {
  if (source.trim().length === 0) return undefined;
  const numbered = source.split(/\r?\n/).map((line, index) => `${index + 1}: ${line}`);
  const full = numbered.join("\n");
  if (full.length <= AGENT_SOURCE_CONTEXT_MAX) return full;

  const head: string[] = [];
  let headChars = 0;
  for (const line of numbered) {
    if (headChars + line.length > Math.floor(AGENT_SOURCE_CONTEXT_MAX * 0.72)) break;
    head.push(line);
    headChars += line.length + 1;
  }

  const tail: string[] = [];
  let tailChars = 0;
  for (let i = numbered.length - 1; i >= 0; i--) {
    const line = numbered[i]!;
    if (tailChars + line.length > Math.floor(AGENT_SOURCE_CONTEXT_MAX * 0.22)) break;
    tail.unshift(line);
    tailChars += line.length + 1;
  }

  const omitted = Math.max(0, numbered.length - head.length - tail.length);
  return [...head, `... (${omitted} lines omitted) ...`, ...tail].join("\n");
}

function diagnosticsForAgent(previewDiagnostics: readonly string[], canvasValidation: CanvasValidationState): string[] {
  const preview = previewDiagnostics
    .map((d) => d.trim())
    .filter(Boolean)
    .map((d) => `[preview compile] ${d}`);
  const canvasDiagnostics = canvasValidation.status === "invalid" ? (canvasValidation.diagnostics ?? []) : [];
  const canvas = canvasDiagnostics
    .map((d) => d.trim())
    .filter(Boolean)
    .map((d) => `[canvas validation] ${d}`);
  return [...preview, ...canvas].slice(0, 8);
}

function selectedContextForAgent(source: string, selectedLine: number | null): Pick<
  AgentRunContext,
  "selectedLine" | "selectedNodeSummary" | "selectedSourceWindow"
> {
  if (!selectedLine || selectedLine < 1) return {};
  const lines = source.split(/\r?\n/);
  const start = Math.max(1, selectedLine - AGENT_SELECTED_CONTEXT_RADIUS);
  const end = Math.min(lines.length, selectedLine + AGENT_SELECTED_CONTEXT_RADIUS);
  const selectedSourceWindow = lines
    .slice(start - 1, end)
    .map((line, index) => {
      const lineNo = start + index;
      return `${lineNo}${lineNo === selectedLine ? ">" : ":"} ${line}`;
    })
    .join("\n");
  const block = parseLsBlocks(source).find((b) => b.line === selectedLine);
  return {
    selectedLine,
    ...(block ? { selectedNodeSummary: describeBlock(block) } : {}),
    selectedSourceWindow,
  };
}

function readSelectedProject(): string | undefined {
  try {
    return window.localStorage.getItem("lunaria_project") ?? undefined;
  } catch {
    return undefined;
  }
}

function persistSelectedProject(id: string | undefined): void {
  try {
    if (id) window.localStorage.setItem("lunaria_project", id);
    else window.localStorage.removeItem("lunaria_project");
  } catch {
    // ignore storage failures
  }
}

const STARTER_PROMPTS: readonly Entry[] = [
  {
    zh: "写一个校园故事的开场：转学第一天遇到疏离清冷的学生会长。",
    en: "Open a campus story: on the first day at a new school, the lead meets the aloof student council president.",
  },
  {
    zh: "三年后重逢的初恋，写一段带好感度分支的咖啡馆对话。",
    en: "Reunited with a first love after three years — a café scene with affection branches.",
  },
  {
    zh: "续写上一章，加入一个需要玩家做选择的关键抉择。",
    en: "Continue the last chapter, adding a pivotal choice the player must make.",
  },
];

// App-level bilingual strings (episode stub, adapt turn label, default book name).
const APP_S = {
  adaptTurn: { zh: "从上传的小说改编第一章…", en: "Adapting the first chapter of the uploaded novel…" },
  bookName: { zh: "未命名作品 {n}", en: "Untitled book {n}" },
  guestAiDisabled: {
    zh: "访客模式可以体验编辑器和预览；AI 创作与小说改编需要登录账号。",
    en: "Guest mode can explore the editor and preview; AI writing and novel adaptation require signing in.",
  },
  noProjectAiDisabled: { zh: "作品还在加载，稍后再让 AI 修改。", en: "The project is still loading. Try AI again shortly." },
  noEpisodeAiDisabled: { zh: "先选择或创建一个剧集，再让 AI 修改。", en: "Select or create an episode before asking AI to edit." },
  sourceLoadingAiDisabled: { zh: "剧集内容还在载入，稍后再让 AI 修改。", en: "The episode source is still loading. Try AI again shortly." },
  viewEditor: { zh: "卡片", en: "Cards" },
  viewCode: { zh: "代码", en: "Code" },
  viewStudio: { zh: "工作室", en: "Studio" },
  viewCanvas: { zh: "画布", en: "Canvas" },
  viewAssets: { zh: "素材", en: "Assets" },
  viewPreview: { zh: "试玩", en: "Playtest" },
  agentChipEpisode: { zh: "剧集", en: "Episode" },
  agentChipView: { zh: "视图", en: "View" },
  agentChipSelected: { zh: "选中", en: "Selected" },
  agentChipDiagnostics: { zh: "校验", en: "Checks" },
  agentChipLine: { zh: "第 {line} 行", en: "Line {line}" },
  agentChipIssueCount: { zh: "{count} 个问题", en: "{count} issues" },
  confirmCreateTitle: { zh: "先确认创作方向", en: "Confirm the creative direction" },
  confirmCreateSummary: {
    zh: "我会先整理世界观、角色关系、路线结构和首集核心选择，再写成可编译的剧集。",
    en: "I will first shape the world, characters, route structure, and first key choice, then draft a compilable episode.",
  },
  confirmCreateStep1: { zh: "建立故事骨架：世界观、主角、可攻略角色、主要冲突。", en: "Build the story spine: world, protagonist, romanceable leads, and main conflict." },
  confirmCreateStep2: { zh: "规划关系变化、后面会记住的选择，以及后续剧情去向。", en: "Plan relationship changes, remembered choices, and later story direction." },
  confirmCreateStep3: { zh: "写入首集，编译校验，并给出质量自评。", en: "Write the first episode, compile-check it, and report a quality self-check." },
  confirmCreateWorldQuestion: { zh: "先按这条需求扩展成一个可玩的世界观核心吗？", en: "Should I turn this request into a playable story core first?" },
  confirmCreateWorldOptionDirect: { zh: "按需求扩展", en: "Expand the request" },
  confirmCreateWorldOptionDirectDesc: { zh: "推荐：保留你的方向，补足舞台、冲突和钩子。", en: "Recommended: keep your direction and fill in setting, conflict, and hook." },
  confirmCreateWorldOptionLight: { zh: "轻量试写", en: "Light trial" },
  confirmCreateWorldOptionLightDesc: { zh: "只先做首集需要的信息，不展开完整世界观。", en: "Only create what episode one needs, without a full world bible." },
  confirmCreateRoutesQuestion: { zh: "角色路线先按 1 个主角 + 2 位可攻略角色规划吗？", en: "Should I plan 1 protagonist and 2 romanceable leads first?" },
  confirmCreateRoutesOptionTwo: { zh: "2 位可攻略角色", en: "2 romance routes" },
  confirmCreateRoutesOptionTwoDesc: { zh: "推荐：路线对比清楚，首集选择也更有代价。", en: "Recommended: clear contrast and a costlier first choice." },
  confirmCreateRoutesOptionOne: { zh: "1 位可攻略角色", en: "1 romance route" },
  confirmCreateRoutesOptionOneDesc: { zh: "更简单，适合先做短篇试水。", en: "Simpler, better for a short pilot." },
  confirmCreateTaskQuestion: { zh: "首集任务先写“相遇 + 一个有代价的选择”吗？", en: "Should episode one focus on a meeting plus one costly choice?" },
  confirmCreateTaskOptionChoice: { zh: "相遇 + 核心选择", en: "Meeting + key choice" },
  confirmCreateTaskOptionChoiceDesc: { zh: "推荐：最快让玩家理解关系和分叉。", en: "Recommended: quickly teaches the relationship and branch." },
  confirmCreateTaskOptionAtmosphere: { zh: "先铺氛围", en: "Set atmosphere first" },
  confirmCreateTaskOptionAtmosphereDesc: { zh: "节奏更慢，首集选择会更靠后。", en: "Slower pacing; the first choice lands later." },
  confirmEditTitle: { zh: "确认修改当前剧集", en: "Confirm the episode edit" },
  confirmEditSummary: {
    zh: "我会基于当前剧集、选中节点和校验信息做定向修改，保留代码模式可验证。",
    en: "I will make a scoped edit from the current episode, selected node, and checks while keeping the source verifiable.",
  },
  confirmEditStep1: { zh: "读取当前剧集上下文和选中位置。", en: "Read the current episode context and selected position." },
  confirmEditStep2: { zh: "按你的指令修改剧情、分支或后续剧情去向，不重写无关内容。", en: "Edit the requested story, branch, or later direction without rewriting unrelated content." },
  confirmEditStep3: { zh: "写入后编译校验；本次剧集修改可一键撤销。", en: "Compile-check after writing; this episode change can be undone." },
  confirmEditScopeQuestion: { zh: "这次要只改选中附近，还是会影响后续剧情去向？", en: "Should this only edit the selected area, or affect later story direction?" },
  confirmEditScopeOptionLocal: { zh: "只改选中附近", en: "Selected area only" },
  confirmEditScopeOptionLocalDesc: { zh: "推荐：润色或补写，不改变后续剧情去向。", en: "Recommended: polish or extend without changing later story direction." },
  confirmEditScopeOptionEpisode: { zh: "重写当前剧集", en: "Rewrite this episode" },
  confirmEditScopeOptionEpisodeDesc: { zh: "保留后续剧情去向，重写表达和节奏。", en: "Keep later direction, rewrite expression and pacing." },
  confirmEditScopeOptionRoute: { zh: "同步改剧情去向", en: "Change story direction too" },
  confirmEditScopeOptionRouteDesc: { zh: "会改后续剧情、角色关系规则或故事大纲。", en: "May change later plot, relationship rules, or the story outline." },
  confirmAdaptTitle: { zh: "确认改编方式", en: "Confirm the adaptation plan" },
  confirmAdaptSummary: {
    zh: "我会先提炼原文故事核心、角色和改编风险，再把第一章改成可玩的互动剧集。",
    en: "I will first extract the story core, characters, and adaptation risks, then turn chapter one into a playable episode.",
  },
  confirmAdaptStep1: { zh: "提炼故事核心：主题、调性、核心冲突、候选可攻略角色。", en: "Extract story core: theme, tone, core conflict, and candidate romanceable leads." },
  confirmAdaptStep2: { zh: "建立角色与路线计划，写入故事大纲。", en: "Create the character and route plan, then persist the story outline." },
  confirmAdaptStep3: { zh: "改编第一章，写入剧集并编译校验。", en: "Adapt chapter one, write the episode, and compile-check it." },
  confirmAdaptWorldQuestion: { zh: "第一轮改编先保留原世界观，还是做轻量本土化？", en: "For the first pass, preserve the original world or lightly localize it?" },
  confirmAdaptWorldOptionKeep: { zh: "保留原世界观", en: "Preserve original world" },
  confirmAdaptWorldOptionKeepDesc: { zh: "推荐：先忠实抓住原文张力，后续再本土化。", en: "Recommended: capture the original tension first, localize later if needed." },
  confirmAdaptWorldOptionLocalize: { zh: "轻量本土化", en: "Light localization" },
  confirmAdaptWorldOptionLocalizeDesc: { zh: "降低解释成本，但会改动设定外壳。", en: "Lowers explanation cost but changes the setting wrapper." },
  confirmAdaptLiQuestion: { zh: "候选可攻略角色先怎么处理？", en: "How should I handle candidate romanceable leads first?" },
  confirmAdaptLiOptionConservative: { zh: "保留核心关系", en: "Keep core relationships" },
  confirmAdaptLiOptionConservativeDesc: { zh: "推荐：不乱扩写，只补互动所需的关系。", en: "Recommended: avoid over-expansion and only add what interaction needs." },
  confirmAdaptLiOptionExpand: { zh: "扩写第二路线", en: "Expand a second route" },
  confirmAdaptLiOptionExpandDesc: { zh: "互动更丰富，但需要补写更多角色弧光。", en: "Richer interaction, but needs more invented character arc." },
  confirmAdaptTaskQuestion: { zh: "第一章先保留原文关键情节，并新增一个玩家选择吗？", en: "Should chapter one keep the key source beats and add one player choice?" },
  confirmAdaptTaskOptionChoice: { zh: "保留关键情节 + 新增选择", en: "Keep key beats + add choice" },
  confirmAdaptTaskOptionChoiceDesc: { zh: "推荐：既忠实原文，也让它变成可玩剧集。", en: "Recommended: faithful to source while making it playable." },
  confirmAdaptTaskOptionFaithful: { zh: "尽量忠实改写", en: "Mostly faithful rewrite" },
  confirmAdaptTaskOptionFaithfulDesc: { zh: "改动最少，但互动性会弱一些。", en: "Smallest changes, but weaker interaction." },
  confirmContextRequest: { zh: "需求", en: "Request" },
  confirmContextEpisode: { zh: "剧集", en: "Episode" },
  confirmContextView: { zh: "视图", en: "View" },
  confirmContextSelection: { zh: "选中", en: "Selection" },
  confirmContextDiagnostics: { zh: "校验", en: "Checks" },
  confirmContextFile: { zh: "文件", en: "File" },
  confirmContextLength: { zh: "长度", en: "Length" },
  confirmContextNewBook: { zh: "新作品", en: "New book" },
  confirmNewEpisodeValue: { zh: "将创建首集", en: "First episode will be created" },
  confirmChars: { zh: "{count} 字符", en: "{count} chars" },
  confirmedCheckpointPrefix: { zh: "已确认", en: "Confirmed" },
  confirmedCheckpointFallback: { zh: "使用推荐默认", en: "Use recommended default" },
} satisfies Record<string, Entry>;

function clipForConfirmation(value: string, max = 72): string {
  const compact = value.replace(/\s+/g, " ").trim();
  if (compact.length <= max) return compact;
  return `${compact.slice(0, Math.max(0, max - 1))}…`;
}

function confirmationIssueCount(diagnostics: readonly string[], canvasValidation: CanvasValidationState): number {
  return diagnosticsForAgent(diagnostics, canvasValidation).length;
}

export function agentConfirmationKindForRequest(args: {
  readonly activeEpisodeId?: string;
  readonly sourceReady: boolean;
  readonly prompt: string;
}): AgentConfirmationKind {
  if (isAdaptationRequest(args)) return "adapt";
  if (!args.activeEpisodeId || !args.sourceReady) return "create";
  const lower = args.prompt.toLowerCase();
  if (/(从零|新故事|新作品|start from scratch|new story|from scratch)/i.test(lower)) return "create";
  return "edit";
}

function isAdaptationRequest(args: {
  readonly activeEpisodeId?: string;
  readonly sourceReady: boolean;
  readonly prompt: string;
}): boolean {
  const text = args.prompt.trim();
  const hasAdaptIntent = /(改编|adapt|adaptation|turn.+into|rewrite.+as)/i.test(text);
  const hasSourceSignal =
    /(上传的小说|小说|原文|原著|原书|源文本|小说章节|章节|source text|source chapter|novel|chapter|chapter\s+\d+)/i.test(text) ||
    /(以下|下面)(这段|这篇|内容|文本|原文|章节)/i.test(text) ||
    /\b(pasted|below)\s+(source|text|chapter)\b/i.test(text);
  const hasInteractiveConversion =
    /(改编成|改成|做成|转成).{0,16}(互动剧集|互动剧|视觉小说|visual novel|interactive)/i.test(text) ||
    /\b(turn|adapt|rewrite).{0,80}\b(into|as).{0,40}\b(interactive|visual novel)\b/i.test(text);
  if (!hasAdaptIntent && !hasInteractiveConversion) return false;
  if (hasSourceSignal) return true;
  const currentEditHint =
    Boolean(args.activeEpisodeId && args.sourceReady) &&
    /(当前|本集|选中|节点|这里|这句|这段对白|这段对话|对白|台词|润色|更暧昧|更自然|更紧张|current scene|selected|dialogue|polish)/i.test(text);
  if (currentEditHint && !hasInteractiveConversion) {
    return false;
  }
  if (hasInteractiveConversion && text.length > 600) {
    return true;
  }
  return false;
}

const HIGH_RISK_AGENT_PREFLIGHT_RE =
  /(路线|路由|后续剧情|结局|分支|锁线|固定感情线|好感|信号|状态|大纲|角色设定|世界观|重写整集|整集|新建剧集|删除剧集|route|gate|ending|branch|affection|signal|state|story plan|character bible|rewrite episode|whole episode|create scene|delete scene)/i;

export function shouldUseLocalPreflight(args: {
  readonly kind: AgentConfirmationKind;
  readonly prompt: string;
}): boolean {
  if (args.kind === "create" || args.kind === "adapt") return true;
  return HIGH_RISK_AGENT_PREFLIGHT_RE.test(args.prompt);
}

export function buildAgentConfirmationBrief(args: {
  readonly kind: AgentConfirmationKind;
  readonly locale: Parameters<typeof translate>[1];
  readonly prompt: string;
  readonly activeEpisodeId?: string;
  readonly activeViewLabel?: string;
  readonly selectedLine?: number | null;
  readonly diagnosticsCount?: number;
  readonly fileName?: string;
  readonly textLength?: number;
}): AgentConfirmationBrief {
  const t = (entry: Entry, vars?: Record<string, string | number>) => translate(entry, args.locale, vars);
  const contextItems: AgentConfirmationContextItem[] = [];

  if (args.prompt.trim()) {
    contextItems.push({ label: t(APP_S.confirmContextRequest), value: clipForConfirmation(args.prompt) });
  }
  if (args.fileName) {
    contextItems.push({ label: t(APP_S.confirmContextFile), value: clipForConfirmation(args.fileName, 46) });
  }
  if (typeof args.textLength === "number") {
    contextItems.push({ label: t(APP_S.confirmContextLength), value: t(APP_S.confirmChars, { count: args.textLength }) });
  }
  if (args.activeEpisodeId) {
    contextItems.push({ label: t(APP_S.confirmContextEpisode), value: args.activeEpisodeId });
  } else if (args.kind === "create") {
    contextItems.push({ label: t(APP_S.confirmContextNewBook), value: t(APP_S.confirmNewEpisodeValue) });
  }
  if (args.activeViewLabel) {
    contextItems.push({ label: t(APP_S.confirmContextView), value: args.activeViewLabel });
  }
  if (args.selectedLine) {
    contextItems.push({ label: t(APP_S.confirmContextSelection), value: t(APP_S.agentChipLine, { line: args.selectedLine }) });
  }
  if ((args.diagnosticsCount ?? 0) > 0) {
    contextItems.push({
      label: t(APP_S.confirmContextDiagnostics),
      value: t(APP_S.agentChipIssueCount, { count: args.diagnosticsCount ?? 0 }),
      tone: "danger",
    });
  }

  if (args.kind === "adapt") {
    return {
      kind: "adapt",
      title: t(APP_S.confirmAdaptTitle),
      summary: t(APP_S.confirmAdaptSummary),
      contextItems,
      questions: [
        {
          id: "adapt_world_shift",
          question: t(APP_S.confirmAdaptWorldQuestion),
          suggestedAnswer: t(APP_S.confirmAdaptWorldOptionKeep),
          options: [
            {
              id: "keep_world",
              label: t(APP_S.confirmAdaptWorldOptionKeep),
              value: t(APP_S.confirmAdaptWorldOptionKeep),
              description: t(APP_S.confirmAdaptWorldOptionKeepDesc),
            },
            {
              id: "light_localization",
              label: t(APP_S.confirmAdaptWorldOptionLocalize),
              value: t(APP_S.confirmAdaptWorldOptionLocalize),
              description: t(APP_S.confirmAdaptWorldOptionLocalizeDesc),
            },
          ],
        },
        {
          id: "adapt_li_selection",
          question: t(APP_S.confirmAdaptLiQuestion),
          suggestedAnswer: t(APP_S.confirmAdaptLiOptionConservative),
          options: [
            {
              id: "conservative",
              label: t(APP_S.confirmAdaptLiOptionConservative),
              value: t(APP_S.confirmAdaptLiOptionConservative),
              description: t(APP_S.confirmAdaptLiOptionConservativeDesc),
            },
            {
              id: "expand_second_route",
              label: t(APP_S.confirmAdaptLiOptionExpand),
              value: t(APP_S.confirmAdaptLiOptionExpand),
              description: t(APP_S.confirmAdaptLiOptionExpandDesc),
            },
          ],
        },
        {
          id: "adapt_chapter_task",
          question: t(APP_S.confirmAdaptTaskQuestion),
          suggestedAnswer: t(APP_S.confirmAdaptTaskOptionChoice),
          options: [
            {
              id: "beats_plus_choice",
              label: t(APP_S.confirmAdaptTaskOptionChoice),
              value: t(APP_S.confirmAdaptTaskOptionChoice),
              description: t(APP_S.confirmAdaptTaskOptionChoiceDesc),
            },
            {
              id: "faithful_rewrite",
              label: t(APP_S.confirmAdaptTaskOptionFaithful),
              value: t(APP_S.confirmAdaptTaskOptionFaithful),
              description: t(APP_S.confirmAdaptTaskOptionFaithfulDesc),
            },
          ],
        },
      ],
      steps: [t(APP_S.confirmAdaptStep1), t(APP_S.confirmAdaptStep2), t(APP_S.confirmAdaptStep3)],
    };
  }

  if (args.kind === "create") {
    return {
      kind: "create",
      title: t(APP_S.confirmCreateTitle),
      summary: t(APP_S.confirmCreateSummary),
      contextItems,
      questions: [
        {
          id: "world_premise",
          question: t(APP_S.confirmCreateWorldQuestion),
          suggestedAnswer: t(APP_S.confirmCreateWorldOptionDirect),
          options: [
            {
              id: "expand_request",
              label: t(APP_S.confirmCreateWorldOptionDirect),
              value: t(APP_S.confirmCreateWorldOptionDirect),
              description: t(APP_S.confirmCreateWorldOptionDirectDesc),
            },
            {
              id: "light_trial",
              label: t(APP_S.confirmCreateWorldOptionLight),
              value: t(APP_S.confirmCreateWorldOptionLight),
              description: t(APP_S.confirmCreateWorldOptionLightDesc),
            },
          ],
        },
        {
          id: "character_routes",
          question: t(APP_S.confirmCreateRoutesQuestion),
          suggestedAnswer: t(APP_S.confirmCreateRoutesOptionTwo),
          options: [
            {
              id: "two_routes",
              label: t(APP_S.confirmCreateRoutesOptionTwo),
              value: t(APP_S.confirmCreateRoutesOptionTwo),
              description: t(APP_S.confirmCreateRoutesOptionTwoDesc),
            },
            {
              id: "one_route",
              label: t(APP_S.confirmCreateRoutesOptionOne),
              value: t(APP_S.confirmCreateRoutesOptionOne),
              description: t(APP_S.confirmCreateRoutesOptionOneDesc),
            },
          ],
        },
        {
          id: "chapter_task",
          question: t(APP_S.confirmCreateTaskQuestion),
          suggestedAnswer: t(APP_S.confirmCreateTaskOptionChoice),
          options: [
            {
              id: "meeting_choice",
              label: t(APP_S.confirmCreateTaskOptionChoice),
              value: t(APP_S.confirmCreateTaskOptionChoice),
              description: t(APP_S.confirmCreateTaskOptionChoiceDesc),
            },
            {
              id: "atmosphere_first",
              label: t(APP_S.confirmCreateTaskOptionAtmosphere),
              value: t(APP_S.confirmCreateTaskOptionAtmosphere),
              description: t(APP_S.confirmCreateTaskOptionAtmosphereDesc),
            },
          ],
        },
      ],
      steps: [t(APP_S.confirmCreateStep1), t(APP_S.confirmCreateStep2), t(APP_S.confirmCreateStep3)],
    };
  }

  return {
    kind: "edit",
    title: t(APP_S.confirmEditTitle),
    summary: t(APP_S.confirmEditSummary),
    contextItems,
    questions: [
      {
        id: "edit_scope",
        question: t(APP_S.confirmEditScopeQuestion),
        suggestedAnswer: t(APP_S.confirmEditScopeOptionLocal),
        options: [
          {
            id: "local_polish",
            label: t(APP_S.confirmEditScopeOptionLocal),
            value: t(APP_S.confirmEditScopeOptionLocal),
            description: t(APP_S.confirmEditScopeOptionLocalDesc),
          },
          {
            id: "episode_rewrite",
            label: t(APP_S.confirmEditScopeOptionEpisode),
            value: t(APP_S.confirmEditScopeOptionEpisode),
            description: t(APP_S.confirmEditScopeOptionEpisodeDesc),
          },
          {
            id: "route_plan",
            label: t(APP_S.confirmEditScopeOptionRoute),
            value: t(APP_S.confirmEditScopeOptionRoute),
            description: t(APP_S.confirmEditScopeOptionRouteDesc),
          },
        ],
      },
    ],
    steps: [t(APP_S.confirmEditStep1), t(APP_S.confirmEditStep2), t(APP_S.confirmEditStep3)],
  };
}

function appendConfirmedAnswers(
  lines: string[],
  heading: string,
  brief?: AgentConfirmationBrief,
  answers?: readonly AgentConfirmationAnswer[],
): void {
  const questions = brief?.questions ?? [];
  if (questions.length === 0) return;
  const answerByKey = new Map<string, string>();
  for (const answer of answers ?? []) {
    const key = answer.id ?? answer.question;
    const value = answer.answer.trim();
    if (value) answerByKey.set(key, value);
  }
  lines.push("", heading);
  for (const question of questions) {
    const answer = answerByKey.get(question.id ?? question.question) ?? question.suggestedAnswer ?? "User accepted the recommended default.";
    lines.push(`- ${question.question}`);
    lines.push(`  Answer: ${answer}`);
  }
}

export function confirmationAnswerSummary(
  brief: AgentConfirmationBrief,
  answers?: readonly AgentConfirmationAnswer[],
  fallback = "Use recommended default",
): string {
  const questions = brief.questions ?? [];
  if (questions.length === 0) return "";
  const answerByKey = new Map<string, string>();
  for (const answer of answers ?? []) {
    const key = answer.id ?? answer.question;
    const value = answer.answer.trim();
    if (value) answerByKey.set(key, value);
  }
  return questions
    .slice(0, 3)
    .map((question) => {
      const answer = answerByKey.get(question.id ?? question.question) ?? question.suggestedAnswer ?? fallback;
      return `${question.question} → ${answer}`;
    })
    .join("\n");
}

export function confirmedCheckpointDisplayPrompt(
  brief: AgentConfirmationBrief,
  locale: Parameters<typeof translate>[1],
  answers?: readonly AgentConfirmationAnswer[],
): string {
  const summary = confirmationAnswerSummary(brief, answers, translate(APP_S.confirmedCheckpointFallback, locale));
  return summary
    ? `${translate(APP_S.confirmedCheckpointPrefix, locale)}：${brief.title}\n${summary}`
    : `${translate(APP_S.confirmedCheckpointPrefix, locale)}：${brief.title}`;
}

export function confirmedAgentInstruction(
  kind: AgentConfirmationKind,
  userPrompt: string,
  args: {
    readonly brief?: AgentConfirmationBrief;
    readonly answers?: readonly AgentConfirmationAnswer[];
    readonly userNote?: string;
  } = {},
): string {
  const workflow =
    kind === "create"
      ? [
          "The user confirmed a from-scratch creation flow.",
          "This start confirmation is not a substitute for creative alignment. If the world, MC, love-interest routes, episode count, or route-lock plan is still ambiguous, call request_confirmation with concrete questions and stop before write_plan/write_scene.",
          "Before writing, read_plan. If the plan is missing or skeletal, load character-architect and entity-planner before write_plan; load episode-writer and lunascript only when drafting .ls.",
          "Persist the creative spine with write_plan: premise/world, MC goal/fear, LI route promises, character Bible essentials, episode tasks, signal/gate ledgers, locations, wardrobe, and poses.",
          "Then write a complete, compilable first episode with one meaningful @choice and a tail @gate. Compile and fix diagnostics. Finish with report_quality.",
        ]
      : kind === "adapt"
        ? [
            "The user confirmed a source-adaptation flow from the free-form agent prompt.",
            "This local confirmation is consent to inspect the pasted/source material, not creative alignment. First load novel-evaluator and identify story core, tone, protagonist, candidate romanceable leads, and adaptation risks from the user-provided source.",
            "If no actual source text is present in the prompt or workspace context, do not invent one. Ask the user to paste/upload the source via request_confirmation and stop.",
            "If localization, romanceable-lead selection, fictional expansion, fate/forced-love rewrite, or the first episode task is still inferred, call request_confirmation with concrete options and stop before write_plan/write_scene.",
            "After the user confirms those creative choices, load character-architect and entity-planner as needed, then persist a complete story plan with write_plan.",
            "Only then load episode-writer and lunascript for the actual .ls episode. Compile changed scenes and finish with report_quality.",
          ]
      : [
          "The user confirmed a scoped edit flow.",
          "If this edit changes gate routing, route ownership, affection rules, or more than the selected/local area and the intended outcome is not clear, call request_confirmation with concrete tradeoffs and stop before writing.",
          "Use the provided current episode/source excerpt, selected line, diagnostics, and story plan. Load episode-writer and lunascript via read_skill when changing .ls.",
          "Keep unrelated content stable. Patch only the needed episode(s), compile-check changed scenes, and finish with report_quality.",
        ];

  const lines = [
    "CONFIRMED USER INTENT",
    userPrompt.trim(),
    "",
    "CONFIRMED WORKFLOW",
    ...workflow.map((line, index) => `${index + 1}. ${line}`),
  ];
  appendConfirmedAnswers(lines, "CONFIRMED LOCAL ANSWERS", args.brief, args.answers);
  const note = args.userNote?.trim();
  if (note) {
    lines.push("", "USER NOTES ADDED DURING LOCAL CONFIRMATION", note);
  }
  return lines.join("\n");
}

export function confirmedAdaptInstruction(
  userNote?: string,
  args: {
    readonly brief?: AgentConfirmationBrief;
    readonly answers?: readonly AgentConfirmationAnswer[];
  } = {},
): string {
  const lines = [
    "CONFIRMED USER INTENT",
    "The user confirmed an adaptation flow for the uploaded source text.",
    "",
    "CONFIRMED WORKFLOW",
    "1. This local confirmation is consent to start, not creative alignment. First read the source text and load novel-evaluator.",
    "2. Extract premise, tone, MC, candidate LIs, and adaptation risks. If localization, LI selection, fictional expansion, or fate/forced-love rewrites are not already clear, call request_confirmation with concrete defaults and stop before write_plan/write_scene.",
    "3. After the user confirms those creative choices, load character-architect and entity-planner as needed, then persist a complete story plan with write_plan.",
    "4. Load episode-writer and lunascript only when drafting the first .ls episode. Compile changed scenes and finish with report_quality.",
  ];
  appendConfirmedAnswers(lines, "CONFIRMED LOCAL ANSWERS", args.brief, args.answers);
  const note = userNote?.trim();
  if (note) {
    lines.push("", "USER NOTES ADDED DURING LOCAL CONFIRMATION", note);
  }
  return lines.join("\n");
}

export function agentConfirmationBriefFromRequest(
  request: Extract<AgentEvent, { type: "confirmation.request" }>["request"],
): AgentConfirmationBrief {
  return {
    kind: request.kind,
    title: request.title,
    summary: request.summary,
    contextItems: request.contextItems ?? [],
    questions: request.questions,
    steps: request.steps ?? [],
  };
}

export function confirmedAgentCheckpointInstruction(args: {
  readonly brief: AgentConfirmationBrief;
  readonly originalPrompt: string;
  readonly userNote?: string;
  readonly answers?: readonly AgentConfirmationAnswer[];
}): string {
  const lines = [
    "CONFIRMED AGENT CHECKPOINT",
    args.brief.title,
    "",
    "ORIGINAL USER REQUEST",
    args.originalPrompt.trim(),
  ];
  if (args.brief.contextItems.length > 0) {
    lines.push("", "CONFIRMED CONTEXT");
    for (const item of args.brief.contextItems) {
      lines.push(`- ${item.label}: ${item.value}`);
    }
  }
  lines.push("", "CONFIRMED ANSWERS");
  const questions = args.brief.questions ?? [];
  const answerByKey = new Map<string, string>();
  for (const answer of args.answers ?? []) {
    const key = answer.id ?? answer.question;
    const value = answer.answer.trim();
    if (value) answerByKey.set(key, value);
  }
  if (questions.length === 0) {
    lines.push("- The user confirmed the agent's proposed direction.");
  } else {
    for (const question of questions) {
      const answer = answerByKey.get(question.id ?? question.question) ?? question.suggestedAnswer ?? "User accepted the agent's recommended default.";
      lines.push(`- ${question.question}`);
      lines.push(`  Answer: ${answer}`);
    }
  }
  const note = args.userNote?.trim();
  if (note) {
    lines.push("", "USER NOTES ADDED DURING CONFIRMATION", note);
  }
  lines.push(
    "",
    "CONTINUE NOW",
    "Do not ask the same checkpoint again. Use the confirmed answers as requirements, load only the skills needed for the next step, then proceed to write_plan/write_scene only after applying those answers. Compile changed scenes and finish with report_quality after writing.",
  );
  return lines.join("\n");
}

function storageIdFromBranchKey(branchKey: string, existingIds: readonly string[]): string {
  const clean = branchKey
    .trim()
    .replace(/[^A-Za-z0-9._-]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 96);
  const base = /^[A-Za-z0-9]/.test(clean) && clean.length > 0 ? clean : "scene";
  const used = new Set(existingIds);
  if (!used.has(base)) return base;
  for (let i = 2; i < 1000; i++) {
    const id = `${base}-${i}`;
    if (!used.has(id)) return id;
  }
  return `scene-${Date.now()}`;
}

function sceneStubForBranchKey(branchKey: string): string {
  return [
    `@episode ${branchKey} "${branchKey}" {`,
    "  NARRATOR: (Continue your story here…)",
    "  @gate { @end to_be_continued }",
    "}",
  ].join("\n");
}

// Compile-verified example episode — offered (opt-in) from onboarding so the whole
// pipeline (compile → step-through preview → asset binding) is explorable without AI.
const DEMO_EPISODE = `@episode cafe:01 "Reunion" {
  @bg set cafe_afternoon fade
  &music warm_nostalgia
  &rin smile
  NARRATOR: Three years later, she is still sitting at our old table.
  RIN: You came. I thought you wouldn't.
  YOU: I made a promise.
  @choice {
    @option A brave "Walk over and sit down" {
      check {
        attr: CHA
        dc: 10
      }
      @if (check.success) {
        @affection rin +2
        @signal mark CAFE_SAT
        RIN [happy]: ...Thank you for remembering.
      } @else {
        RIN [worried]: You still seem unsure.
      }
    }
    @option B safe "Stay where you are" {
      RIN [sad]: Still hesitating, I see.
    }
  }
  @gate {
    @if (affection.rin >= 2): @next cafe:02
    @else: @next cafe:01b
  }
}
`;

export function App(): ReactElement {
  const [token, setToken] = useState(readToken());
  const client = useMemo(() => new LunariaClient({ token }), [token]);

  // current UI locale (for naming created books / stub content at creation time)
  const { locale } = useLocale();
  const localeRef = useRef(locale);
  useEffect(() => {
    localeRef.current = locale;
  }, [locale]);

  // ── auth ──
  const [loginError, setLoginError] = useState<string | undefined>(undefined);
  const [loggingIn, setLoggingIn] = useState(false);
  const [signInOpen, setSignInOpen] = useState(false);
  const openSignIn = useCallback(() => { setLoginError(undefined); setSignInOpen(true); }, []);
  const [accountName, setAccountName] = useState<string | undefined>(undefined);
  const [accountUsage, setAccountUsage] = useState<AccountUsage | null>(null);
  const [accountUsageLoading, setAccountUsageLoading] = useState(false);
  const [accountUsageError, setAccountUsageError] = useState<string | null>(null);

  const persistToken = useCallback((t: string) => {
    try {
      if (t) window.localStorage.setItem("lunaria_token", t);
      else window.localStorage.removeItem("lunaria_token");
    } catch {
      // ignore storage failures
    }
    setToken(t);
  }, []);

  const onLogin = useCallback(
    async (username: string, password: string) => {
      setLoggingIn(true);
      setLoginError(undefined);
      try {
        persistToken(await client.login(username, password));
        setSignInOpen(false);
      } catch (err) {
        setLoginError(messageOf(err));
      } finally {
        setLoggingIn(false);
      }
    },
    [client, persistToken],
  );
  const onRegister = useCallback(
    async (username: string, password: string, inviteCode: string) => {
      setLoggingIn(true);
      setLoginError(undefined);
      try {
        // Invite-code sign-up returns a token immediately (auto-login), so we
        // persist it exactly like a successful login.
        persistToken(await client.register(username, password, inviteCode));
        setSignInOpen(false);
      } catch (err) {
        setLoginError(messageOf(err));
      } finally {
        setLoggingIn(false);
      }
    },
    [client, persistToken],
  );
  const onGuest = useCallback(() => persistToken("dev"), [persistToken]);
  const onLogout = useCallback(() => {
    setAccountName(undefined);
    setAccountUsage(null);
    setAccountUsageError(null);
    setAccountUsageLoading(false);
    persistToken("");
  }, [persistToken]);

  const refreshAccountUsage = useCallback(async () => {
    if (!token || token === "dev") {
      setAccountUsage(null);
      setAccountUsageError(null);
      setAccountUsageLoading(false);
      return;
    }
    setAccountUsageLoading(true);
    setAccountUsageError(null);
    try {
      setAccountUsage(await client.accountUsage());
    } catch (err) {
      setAccountUsage(null);
      setAccountUsageError(messageOf(err));
    } finally {
      setAccountUsageLoading(false);
    }
  }, [client, token]);

  // resolve the display name for a real (non-guest) login
  useEffect(() => {
    let cancelled = false;
    if (!token || token === "dev") {
      setAccountName(undefined);
      return;
    }
    (async () => {
      try {
        const m = await client.me();
        if (!cancelled) setAccountName(m.user?.name ?? m.user?.username);
      } catch {
        // non-fatal — the account chip falls back to a generic label
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [client, token]);

  useEffect(() => {
    void refreshAccountUsage();
  }, [refreshAccountUsage]);

  const [projects, setProjects] = useState<ProjectMeta[]>([]);
  const [projectName, setProjectName] = useState("Lunaria");
  const [projectId, setProjectId] = useState<string | undefined>(undefined);
  const [projectProtagonist, setProjectProtagonist] = useState<string | null>(null);
  const [projectPlanRevision, setProjectPlanRevision] = useState(0);
  const [projectDownloadBusy, setProjectDownloadBusy] = useState(false);
  const [episodes, setEpisodes] = useState<{ id: string }[]>([]);
  const [activeEpisodeId, setActiveEpisodeId] = useState<string | undefined>(undefined);
  const [loaded, setLoaded] = useState(false);
  const [state, setState] = useState<AppState>(initialAppState);
  const [input, setInput] = useState("");
  const [agentUndo, setAgentUndo] = useState<AgentUndoState | null>(null);
  const [agentResult, setAgentResult] = useState<AgentResultState | null>(null);
  const [pendingAgentRun, setPendingAgentRun] = useState<PendingAgentRun | null>(null);
  const [pendingAdaptRun, setPendingAdaptRun] = useState<PendingAdaptRun | null>(null);

  const [agentUndoBusy, setAgentUndoBusy] = useState(false);
  const [canvasSelectedLine, setCanvasSelectedLine] = useState<number | null>(null);
  const [studioSelectedLine, setStudioSelectedLine] = useState<number | null>(null);
  const canvasUndoStackRef = useRef<string[]>([]);
  const [canvasUndoDepth, setCanvasUndoDepth] = useState(0);
  const preservePendingForWorkspaceEntryRef = useRef(false);

  // ── workspace view (工作室 / 卡片 / 画布 / 素材 / 试玩) + agent sidebar ──
  const [activeView, setActiveView] = useState<WorkspaceView>("writing");
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);
  const [companionMode, setCompanionMode] = useState<"agent" | "preview">("preview");
  const [companionOpenKey, setCompanionOpenKey] = useState(0);
  const writingRef = useRef<CowritingHandle>(null);
  const [editorMode, setEditorMode] = useState<EditorMode>("card");
  const [editorInitialLine, setEditorInitialLine] = useState<number | null>(null);

  useEffect(() => {
    if (activeView === "studio" || activeView === "canvas") {
      setSidebarCollapsed(true);
    }
    if (activeView === "canvas") {
      setCanvasSelectedLine(null);
    }
  }, [activeView]);

  useEffect(() => {
    if (preservePendingForWorkspaceEntryRef.current) {
      preservePendingForWorkspaceEntryRef.current = false;
      return;
    }
    setPendingAgentRun(null);
    setPendingAdaptRun(null);
  }, [activeEpisodeId, activeView]);

  // ── editor source (editable) + debounced autosave ──
  const [editorSource, setEditorSource] = useState("");
  const [editorSourceEpisodeId, setEditorSourceEpisodeId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (!projectId) {
      setProjectProtagonist(null);
      return undefined;
    }
    void client.getProtagonist(projectId)
      .then((value) => { if (!cancelled) setProjectProtagonist(value); })
      .catch(() => { if (!cancelled) setProjectProtagonist(null); });
    return () => { cancelled = true; };
  }, [client, projectId, projectPlanRevision]);
  const savedSourceRef = useRef("");

  // ── all scenes' sources (drives the story canvas) ──
  const [allScenes, setAllScenes] = useState<{ id: string; source: string }[]>([]);

  const clearCanvasUndo = useCallback(() => {
    canvasUndoStackRef.current = [];
    setCanvasUndoDepth(0);
  }, []);

  // ── capabilities (image-gen / compiler) + per-episode asset mapping ──
  const [capabilities, setCapabilities] = useState<Capabilities>({ imageGen: false, cutout: false, compiler: false });
  const [styles, setStyles] = useState<StyleMeta[]>([]);
  const [mapping, setMapping] = useState<AssetMapping>(emptyMapping());
  const [assetsBusy, setAssetsBusy] = useState(false);

  // ── preview (shared real engine via PreviewController) ──
  const controllerRef = useRef<PreviewController | null>(null);
  const [stage, setStage] = useState<StageState | null>(null);
  const [decision, setDecision] = useState<PlayerDecision | null>(null);
  const [isDone, setIsDone] = useState(false);
  const [diagnostics, setDiagnostics] = useState<string[]>([]);
  const [compiling, setCompiling] = useState(false);
  const [previewInvalidReason, setPreviewInvalidReason] = useState<PreviewInvalidReason | null>(null);
  const codeDiagnostics = useMemo<CodeDiagnostic[]>(() => diagnostics.map((message) => ({
    line: diagnosticLineFromMessage(message) ?? 1,
    message,
    severity: "error",
  })), [diagnostics]);

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;
    const media = window.matchMedia("(max-width: 640px)");
    const onChange = (event: MediaQueryListEvent): void => {
      if (event.matches) setSidebarCollapsed(true);
    };
    if (media.matches) setSidebarCollapsed(true);
    media.addEventListener("change", onChange);
    return () => media.removeEventListener("change", onChange);
  }, []);

  const [canvasValidation, setCanvasValidation] = useState<CanvasValidationState>({ status: "idle" });
  const canvasValidationSeq = useRef(0);
  const agentSubmittingRef = useRef(false);
  const [agentSubmitting, setAgentSubmitting] = useState(false);
  const [assets, setAssets] = useState<{ id: string; thumbUrl?: string }[]>([]);
  const thumbUrlsRef = useRef<string[]>([]);
  const previewUrlsRef = useRef<string[]>([]); // object URLs for asset:// refs in the live preview

  const studioAssetUriMap = useMemo<Record<string, string>>(() => {
    const urlById = new Map(assets.flatMap((asset) => asset.thumbUrl ? [[asset.id, asset.thumbUrl] as const] : []));
    const next: Record<string, string> = {};
    for (const binding of mapping.backgrounds) {
      const url = urlById.get(binding.assetId);
      if (url) next[`background:${binding.symbol}`] = url;
    }
    for (const character of mapping.characters) {
      for (const look of character.looks) {
        const url = urlById.get(look.assetId);
        if (url) next[`character:${character.id}:${look.symbol}`] = url;
      }
    }
    for (const binding of mapping.cg) {
      const url = urlById.get(binding.assetId);
      if (url) next[`cg:${binding.symbol}`] = url;
    }
    return next;
  }, [assets, mapping]);

  // image symbols referenced by the active episode's source (drives the asset workspace)
  const editorSourceReady = Boolean(activeEpisodeId && editorSourceEpisodeId === activeEpisodeId);
  const activeEditorSource = editorSourceReady ? editorSource : "";

  const parsed = useMemo(() => parseSceneSymbols(activeEditorSource), [activeEditorSource]);

  useEffect(() => {
    setCanvasSelectedLine(null);
    setStudioSelectedLine(null);
    setEditorInitialLine(null);
  }, [activeEpisodeId]);

  // capabilities (does the server have image-gen + a compiler) — gates the asset UI
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    (async () => {
      try {
        const c = await client.getCapabilities();
        if (!cancelled) setCapabilities(c);
      } catch {
        // keep conservative defaults (no image gen)
      }
      try {
        const s = await client.getStyles();
        if (!cancelled) setStyles(s);
      } catch {
        // styles are optional; the generate dialog degrades to prompt-only
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [client, token]);

  // load the books (projects) + the selected book's scenes. No forced demo-seed —
  // a fresh book starts empty so onboarding (Phase A) shows; the example is opt-in.
  // The last-selected book is restored from localStorage across sessions.
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    setLoaded(false);
    (async () => {
      try {
        let list = await client.listProjects();
        if (list.length === 0) {
          await client.createProject(translate(COMMON.myFirstBook, localeRef.current));
          list = await client.listProjects();
        }
        if (cancelled) return;
        setProjects(list);
        const storedId = readSelectedProject();
        const chosen = list.find((p) => p.id === storedId) ?? list[0];
        if (!chosen) return;
        setProjectId(chosen.id);
        setProjectName(chosen.name);
        const scenes = await client.listScenes(chosen.id);
        if (!cancelled) {
          setEpisodes(scenes.map((s) => ({ id: s.id })));
          setLoaded(true);
        }
      } catch (err) {
        if (!cancelled) {
          setLoaded(true);
          reportError(setState, err);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [client, token]);

  // auto-select the first episode so the editor/preview have a target
  useEffect(() => {
    if (!activeEpisodeId && episodes.length > 0) {
      setActiveEpisodeId(episodes[0]!.id);
    }
  }, [episodes, activeEpisodeId]);

  // load the active scene's .ls source into the editable buffer. Agent completion
  // refreshes explicitly in onSend/onAdaptNovel; tying this to status changes can
  // clobber unsaved local edits when a run starts before the debounce fires.
  useEffect(() => {
    let cancelled = false;
    if (!projectId || !activeEpisodeId) {
      setEditorSource("");
      setEditorSourceEpisodeId(null);
      savedSourceRef.current = "";
      return;
    }
    setEditorSource("");
    setEditorSourceEpisodeId(null);
    savedSourceRef.current = "";
    (async () => {
      try {
        const src = await client.readScene(projectId, activeEpisodeId);
        if (!cancelled) {
          setEditorSource(src);
          setEditorSourceEpisodeId(activeEpisodeId);
          savedSourceRef.current = src;
        }
      } catch {
        if (!cancelled) {
          setEditorSource("");
          setEditorSourceEpisodeId(null);
          savedSourceRef.current = "";
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [client, projectId, activeEpisodeId]);

  // load the active episode's saved asset mapping
  useEffect(() => {
    let cancelled = false;
    if (!projectId || !activeEpisodeId) {
      setMapping(emptyMapping());
      return;
    }
    (async () => {
      try {
        const m = await client.getMapping(projectId, activeEpisodeId);
        if (!cancelled) setMapping(m);
      } catch {
        if (!cancelled) setMapping(emptyMapping());
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [client, projectId, activeEpisodeId]);

  // load every scene's source for the story canvas
  const reloadAllScenes = useCallback(async () => {
    if (!projectId) {
      setAllScenes([]);
      return;
    }
    try {
      const scenes = await client.listScenes(projectId);
      const withSrc = await Promise.all(
        scenes.map(async (s) => {
          try {
            return { id: s.id, source: await client.readScene(projectId, s.id) };
          } catch {
            return { id: s.id, source: "" };
          }
        }),
      );
      setAllScenes(withSrc);
    } catch {
      setAllScenes([]);
    }
  }, [client, projectId]);

  useEffect(() => {
    void reloadAllScenes();
  }, [reloadAllScenes, episodes, state.status]);

  // load the project's assets (uploaded/generated art) + resolve image thumbnails
  // (the asset endpoint is authed, so thumbs need an authed fetch → object URL).
  const revokeThumbs = useCallback(() => {
    thumbUrlsRef.current.forEach((u) => URL.revokeObjectURL(u));
    thumbUrlsRef.current = [];
  }, []);

  const revokePreviewUrls = useCallback(() => {
    previewUrlsRef.current.forEach((u) => URL.revokeObjectURL(u));
    previewUrlsRef.current = [];
  }, []);

  const reloadAssets = useCallback(async () => {
    if (!projectId) {
      revokeThumbs();
      setAssets([]);
      return;
    }
    let list: { id: string }[];
    try {
      list = await client.listAssets(projectId);
    } catch {
      revokeThumbs();
      setAssets([]);
      return;
    }
    const created: string[] = [];
    const refs = await Promise.all(
      list.map(async (a) => {
        if (!IMAGE_ASSET_RE.test(a.id)) return { id: a.id };
        try {
          const url = await client.assetObjectUrl(projectId, a.id);
          created.push(url);
          return { id: a.id, thumbUrl: url };
        } catch {
          return { id: a.id };
        }
      }),
    );
    revokeThumbs();
    thumbUrlsRef.current = created;
    setAssets(refs);
  }, [client, projectId, revokeThumbs]);

  useEffect(() => {
    void reloadAssets();
    return () => {
      revokeThumbs();
    };
  }, [reloadAssets, revokeThumbs]);

  // ── preview helpers ──
  const syncPreview = useCallback(() => {
    const pc = controllerRef.current;
    if (!pc) {
      setStage(null);
      setDecision(null);
      setIsDone(false);
      return;
    }
    setStage(pc.stage());
    setDecision(pc.decision());
    setIsDone(pc.isDone());
  }, []);

  const resetPreview = useCallback((reason: PreviewInvalidReason | null = null) => {
    if (reason && controllerRef.current) {
      setPreviewInvalidReason(reason);
      return;
    }
    controllerRef.current = null;
    setStage(null);
    setDecision(null);
    setIsDone(false);
    setDiagnostics([]);
    setPreviewInvalidReason(reason);
    revokePreviewUrls();
  }, [revokePreviewUrls]);

  // The compiler emits bound/generated art as `asset:///<assetId>` urls (project
  // assets are bearer-protected, so the browser can't load them by raw URL).
  // Rewrite those to authed object URLs in-place before the player renders.
  const resolveAssetUrls = useCallback(
    async (output: unknown): Promise<void> => {
      if (!projectId) return;
      const cache = new Map<string, string>();
      const created: string[] = [];
      const resolveOne = async (assetId: string): Promise<string | undefined> => {
        const hit = cache.get(assetId);
        if (hit) return hit;
        try {
          const url = await client.assetObjectUrl(projectId, assetId);
          cache.set(assetId, url);
          created.push(url);
          return url;
        } catch {
          return undefined;
        }
      };
      const walk = async (node: unknown): Promise<void> => {
        if (Array.isArray(node)) {
          for (const n of node) await walk(n);
          return;
        }
        if (node && typeof node === "object") {
          const obj = node as Record<string, unknown>;
          if (typeof obj.url === "string" && obj.url.startsWith("asset:")) {
            const assetId = obj.url.split("/").filter(Boolean).pop();
            if (assetId) {
              const resolved = await resolveOne(decodeURIComponent(assetId));
              if (resolved) obj.url = resolved;
            }
          }
          for (const key of Object.keys(obj)) {
            if (key !== "url") await walk(obj[key]);
          }
        }
      };
      const steps = (output as { steps?: unknown }).steps;
      if (steps) await walk(steps);
      revokePreviewUrls();
      previewUrlsRef.current = created;
    },
    [client, projectId, revokePreviewUrls],
  );

  const selectEpisode = useCallback(
    (id: string) => {
      setActiveEpisodeId(id);
      setCanvasValidation({ status: capabilities.compiler ? "idle" : "unavailable" });
      resetPreview();
    },
    [capabilities.compiler, resetPreview],
  );

  const validateCanvasScene = useCallback(
    async (sceneId: string) => {
      if (!projectId) return;
      if (!capabilities.compiler) {
        setCanvasValidation({ status: "unavailable" });
        return;
      }
      const seq = ++canvasValidationSeq.current;
      setCanvasValidation({ status: "checking" });
      try {
        const result = await client.compileScene(projectId, sceneId);
        if (seq !== canvasValidationSeq.current) return;
        if (result.ok) {
          setCanvasValidation({ status: "valid" });
        } else {
          setCanvasValidation({
            status: "invalid",
            diagnostics: result.diagnostics.map((d) => d.message),
          });
        }
      } catch (err) {
        if (seq !== canvasValidationSeq.current) return;
        setCanvasValidation({ status: "invalid", diagnostics: [messageOf(err)] });
      }
    },
    [capabilities.compiler, client, projectId],
  );

  const validateSceneForAgent = useCallback(
    async (sceneId: string): Promise<CanvasValidationState> => {
      if (!projectId || !capabilities.compiler) {
        const unavailable: CanvasValidationState = { status: "unavailable" };
        setCanvasValidation(unavailable);
        return unavailable;
      }
      const seq = ++canvasValidationSeq.current;
      setCanvasValidation({ status: "checking" });
      try {
        const result = await client.compileScene(projectId, sceneId);
        if (seq !== canvasValidationSeq.current) return { status: "checking" };
        if (result.ok) {
          const valid: CanvasValidationState = { status: "valid" };
          setCanvasValidation(valid);
          return valid;
        }
        const invalid: CanvasValidationState = {
          status: "invalid",
          diagnostics: result.diagnostics.map((d) => d.message),
        };
        setCanvasValidation(invalid);
        return invalid;
      } catch (err) {
        if (seq !== canvasValidationSeq.current) return { status: "checking" };
        const invalid: CanvasValidationState = { status: "invalid", diagnostics: [messageOf(err)] };
        setCanvasValidation(invalid);
        return invalid;
      }
    },
    [capabilities.compiler, client, projectId],
  );

  useEffect(() => {
    if ((activeView !== "canvas" && activeView !== "studio") || !activeEpisodeId) return;
    if (!capabilities.compiler) {
      setCanvasValidation({ status: "unavailable" });
      return;
    }
    if (editorSourceEpisodeId !== activeEpisodeId) {
      setCanvasValidation({ status: "idle" });
      return;
    }
    if (editorSource !== savedSourceRef.current) {
      setCanvasValidation({ status: "checking" });
      return;
    }
    void validateCanvasScene(activeEpisodeId);
  }, [activeEpisodeId, activeView, capabilities.compiler, editorSource, editorSourceEpisodeId, validateCanvasScene]);

  const saveActiveSceneNow = useCallback(async (): Promise<boolean> => {
    if (!projectId || !activeEpisodeId) return true;
    if (editorSourceEpisodeId !== activeEpisodeId) return true;
    if (editorSource === savedSourceRef.current) return true;
    try {
      await client.writeScene(projectId, activeEpisodeId, editorSource);
      savedSourceRef.current = editorSource;
      setAllScenes((prev) => prev.map((s) => (s.id === activeEpisodeId ? { ...s, source: editorSource } : s)));
      resetPreview("source");
      return true;
    } catch (err) {
      reportError(setState, err);
      return false;
    }
  }, [activeEpisodeId, client, editorSource, editorSourceEpisodeId, projectId, resetPreview]);

  const onDownloadProject = useCallback(async () => {
    if (!projectId || projectDownloadBusy) return;
    setProjectDownloadBusy(true);
    try {
      if (!(await saveActiveSceneNow())) return;
      const archive = await client.downloadProject(projectId);
      saveBlobToDisk(archive.blob, archive.filename);
    } catch (err) {
      reportError(setState, err);
    } finally {
      setProjectDownloadBusy(false);
    }
  }, [client, projectDownloadBusy, projectId, saveActiveSceneNow]);

  const openSceneFromServer = useCallback(
    async (sceneId: string) => {
      if (!projectId) return;
      try {
        const src = await client.readScene(projectId, sceneId);
        setActiveEpisodeId(sceneId);
        setEditorSource(src);
        setEditorSourceEpisodeId(sceneId);
        savedSourceRef.current = src;
        setAllScenes((prev) =>
          prev.some((s) => s.id === sceneId)
            ? prev.map((s) => (s.id === sceneId ? { ...s, source: src } : s))
            : [...prev, { id: sceneId, source: src }],
        );
        setCanvasValidation({ status: capabilities.compiler ? "idle" : "unavailable" });
        resetPreview();
      } catch (err) {
        reportError(setState, err);
      }
    },
    [capabilities.compiler, client, projectId, resetPreview],
  );

  const enterWorkspaceForLocalPreflight = useCallback(async (): Promise<boolean> => {
    if (!projectId) return false;
    const willChangeWorkspaceContext = activeView !== "writing" || episodes.length === 0 || !activeEpisodeId;
    if (willChangeWorkspaceContext) preservePendingForWorkspaceEntryRef.current = true;
    setActiveView("writing");
    setSidebarCollapsed(true);

    if (episodes.length > 0) {
      const firstEpisodeId = activeEpisodeId ?? episodes[0]?.id;
      if (firstEpisodeId && !activeEpisodeId) setActiveEpisodeId(firstEpisodeId);
      return true;
    }

    try {
      const draft = await createInitialWorkspaceDraftScene(client, projectId, localeRef.current);
      setEpisodes([{ id: draft.id }]);
      setActiveEpisodeId(draft.id);
      setEditorSource(draft.source);
      setEditorSourceEpisodeId(draft.id);
      savedSourceRef.current = draft.source;
      setAllScenes([{ id: draft.id, source: draft.source }]);
      setCanvasValidation({ status: capabilities.compiler ? "idle" : "unavailable" });
      resetPreview();
      return true;
    } catch (err) {
      preservePendingForWorkspaceEntryRef.current = false;
      reportError(setState, err);
      return false;
    }
  }, [activeEpisodeId, activeView, capabilities.compiler, client, episodes, projectId, resetPreview]);

  const compileSceneForPreview = useCallback(
    async (sceneId: string, opts: { readonly saveActive?: boolean; readonly target?: PreviewPassageTarget } = {}) => {
      if (!projectId) return;
      if (opts.saveActive && !(await saveActiveSceneNow())) return;
      setCompiling(true);
      setDiagnostics([]);
      setPreviewInvalidReason(null);
      try {
        const result = await client.compileScene(projectId, sceneId);
        if (result.ok) {
          await resolveAssetUrls(result.output);
          const controller = new PreviewController(result.output);
          if (opts.target && !controller.seekToPassage(opts.target)) {
            controllerRef.current = null;
            setStage(null);
            setDecision(null);
            setIsDone(false);
            setDiagnostics(["This passage could not be reached in the preview. Check its branch conditions or choose another passage."]);
            return;
          }
          controllerRef.current = controller;
          syncPreview();
        } else {
          controllerRef.current = null;
          setStage(null);
          setDecision(null);
          setIsDone(false);
          setDiagnostics(result.diagnostics.map((d) => d.message));
        }
      } catch (err) {
        setDiagnostics([messageOf(err)]);
      } finally {
        setCompiling(false);
      }
    },
    [client, projectId, resolveAssetUrls, saveActiveSceneNow, syncPreview],
  );

  const onCompile = useCallback(async () => {
    if (!activeEpisodeId) return;
    await compileSceneForPreview(activeEpisodeId, { saveActive: true });
  }, [activeEpisodeId, compileSceneForPreview]);

  const onWritingPreview = useCallback(async () => {
    if (!projectId || !activeEpisodeId) return;
    setCompanionMode("preview");
    setCompanionOpenKey((key) => key + 1);
    try {
      const prepared = await writingRef.current?.preparePreview();
      if (prepared !== undefined) {
        await client.writeScene(projectId, activeEpisodeId, prepared.source);
        savedSourceRef.current = prepared.source;
        setAllScenes((prev) => prev.map((s) => s.id === activeEpisodeId ? { ...s, source: prepared.source } : s));
      } else if (!(await saveActiveSceneNow())) return;
      await compileSceneForPreview(activeEpisodeId, { target: prepared?.target });
    } catch (err) {
      setDiagnostics([messageOf(err)]);
    }
  }, [activeEpisodeId, client, compileSceneForPreview, projectId, saveActiveSceneNow]);

  const onOpenAgentPreview = useCallback(async () => {
    const target = agentResult?.targetSceneId ?? activeEpisodeId;
    if (!target) return;
    if (!(await saveActiveSceneNow())) return;
    if (target !== activeEpisodeId) {
      await openSceneFromServer(target);
    }
    setActiveView("preview");
    await compileSceneForPreview(target);
  }, [activeEpisodeId, agentResult?.targetSceneId, compileSceneForPreview, openSceneFromServer, saveActiveSceneNow]);

  // debounced autosave: when the editor buffer diverges from the saved source, write
  // it back, refresh the canvas's copy, and drop the stale preview.
  useEffect(() => {
    if (!projectId || !activeEpisodeId) return;
    if (editorSourceEpisodeId !== activeEpisodeId) return;
    if (editorSource === savedSourceRef.current) return;
    const handle = setTimeout(async () => {
      try {
        await client.writeScene(projectId, activeEpisodeId, editorSource);
        savedSourceRef.current = editorSource;
        setAllScenes((prev) => prev.map((s) => (s.id === activeEpisodeId ? { ...s, source: editorSource } : s)));
        resetPreview("source");
        if (activeView === "canvas" || activeView === "studio") {
          await validateCanvasScene(activeEpisodeId);
        }
      } catch (err) {
        reportError(setState, err);
      }
    }, AUTOSAVE_MS);
    return () => clearTimeout(handle);
  }, [activeEpisodeId, activeView, client, editorSource, editorSourceEpisodeId, projectId, resetPreview, validateCanvasScene]);

  // ── asset mapping: bind / unbind / generate / upload ──
  const persistMapping = useCallback(
    async (next: AssetMapping) => {
      if (!projectId || !activeEpisodeId) {
        setMapping(next);
        resetPreview("assets");
        return;
      }
      try {
        await client.putMapping(projectId, activeEpisodeId, next);
        setMapping(next);
        resetPreview("assets"); // only publish the new binding after persistence succeeds
      } catch (err) {
        reportError(setState, err);
        throw err;
      }
    },
    [client, projectId, activeEpisodeId, resetPreview],
  );

  const onBind = useCallback(
    (kind: AssetKind, symbol: string, assetId: string, charId?: string) => {
      void persistMapping(bindSymbol(mapping, kind, symbol, assetId, charId)).catch(() => undefined);
    },
    [mapping, persistMapping],
  );

  const onUnbind = useCallback(
    (kind: AssetKind, symbol: string, charId?: string) => {
      void persistMapping(unbindSymbol(mapping, kind, symbol, charId)).catch(() => undefined);
    },
    [mapping, persistMapping],
  );

  const onUpload = useCallback(
    async (files: File[], bind?: { kind: AssetKind; symbol: string; charId?: string }) => {
      if (!projectId) return;
      setAssetsBusy(true);
      let firstBindableId: string | undefined;
      try {
        for (const file of files) {
          const bytes = new Uint8Array(await file.arrayBuffer());
          const requestedId = bind
            ? canonicalAssetId({ kind: bind.kind, symbol: bind.symbol, characterId: bind.charId, sourceName: file.name, contentType: file.type || undefined })
            : canonicalLooseAssetId(file.name, file.type || undefined);
          const storedId = await client.putAsset(projectId, requestedId, bytes, file.type || undefined);
          if (bind && firstBindableId === undefined && isBindableUpload(storedId, bind.kind)) {
            firstBindableId = storedId;
          }
        }
        await reloadAssets();
        if (bind && firstBindableId) {
          await persistMapping(bindSymbol(mapping, bind.kind, bind.symbol, firstBindableId, bind.charId));
        }
      } catch (err) {
        reportError(setState, err);
      } finally {
        setAssetsBusy(false);
        void refreshAccountUsage();
      }
    },
    [client, projectId, reloadAssets, mapping, persistMapping, refreshAccountUsage],
  );

  const onGenerate = useCallback(
    async (args: { kind: AssetKind; symbol: string; charId?: string; prompt: string; family?: string; referenceAssetIds?: string[] }) => {
      if (!projectId) return;
      setAssetsBusy(true);
      try {
        const { assetId } = await client.generateAsset(projectId, {
          prompt: args.prompt,
          kind: args.kind,
          symbol: args.symbol,
          charId: args.charId,
          referenceAssetIds: args.referenceAssetIds,
          family: args.family,
        });
        await reloadAssets();
        await persistMapping(bindSymbol(mapping, args.kind, args.symbol, assetId, args.charId));
      } catch (err) {
        reportError(setState, err);
        throw err;
      } finally {
        setAssetsBusy(false);
      }
    },
    [client, projectId, reloadAssets, mapping, persistMapping],
  );

  const onCutoutAsset = useCallback(
    async (args: { assetId: string; symbol: string; charId: string }): Promise<void> => {
      if (!projectId) return;
      setAssetsBusy(true);
      try {
        const { assetId } = await client.cutoutAsset(projectId, args.assetId, { symbol: args.symbol, charId: args.charId });
        await reloadAssets();
        await persistMapping(bindSymbol(mapping, "character", args.symbol, assetId, args.charId));
      } catch (err) {
        reportError(setState, err);
        throw err;
      } finally {
        setAssetsBusy(false);
      }
    },
    [client, mapping, persistMapping, projectId, reloadAssets],
  );

  // AI drafts an image-gen prompt for the user to review (never auto-generates).
  const onSuggestPrompt = useCallback(
    async (args: {
      kind: AssetKind;
      name: string;
      styleName?: string;
      styleDescription?: string;
      notes?: string;
    }): Promise<string> => {
      if (!projectId) return "";
      return client.suggestAssetPrompt(projectId, {
        kind: args.kind,
        name: args.name,
        styleName: args.styleName,
        styleDescription: args.styleDescription,
        notes: args.notes,
      });
    },
    [client, projectId],
  );

  const onPreviewAssetPrompt = useCallback(
    async (args: { kind: "background" | "character" | "cg"; prompt: string; family?: string; symbol?: string; charId?: string; hasAssetReference?: boolean }): Promise<string> => {
      return (await client.previewAssetPrompt(args)).finalPrompt;
    },
    [client],
  );

  const snapshotProjectScenes = useCallback(
    async (id: string): Promise<SceneSnapshot[]> => {
      const scenes = await client.listScenes(id);
      const snapshots: SceneSnapshot[] = [];
      for (const scene of scenes) {
        snapshots.push({ id: scene.id, source: await client.readScene(id, scene.id) });
      }
      return snapshots;
    },
    [client],
  );

  const onUndoAgentRun = useCallback(async () => {
    if (!projectId || !agentUndo || agentUndoBusy) return;
    setAgentUndoBusy(true);
    try {
      for (const sceneId of agentUndo.createdSceneIds) {
        await client.deleteScene(projectId, sceneId);
      }
      for (const snapshot of agentUndo.snapshots) {
        await client.writeScene(projectId, snapshot.id, snapshot.source);
      }
      const scenes = await client.listScenes(projectId);
      setEpisodes(scenes.map((s) => ({ id: s.id })));
      const restoredIds = new Set(scenes.map((s) => s.id));
      const target =
        (agentUndo.activeEpisodeId && restoredIds.has(agentUndo.activeEpisodeId)
          ? agentUndo.activeEpisodeId
          : undefined) ??
        agentUndo.snapshots.find((s) => restoredIds.has(s.id))?.id ??
        scenes[0]?.id;
      if (target) {
        await openSceneFromServer(target);
      } else {
        setActiveEpisodeId(undefined);
        setEditorSource("");
        setEditorSourceEpisodeId(null);
        savedSourceRef.current = "";
        setAllScenes([]);
        resetPreview();
      }
      setAgentUndo(null);
      setAgentResult(null);
      setActiveView("writing");
    } catch (err) {
      reportError(setState, err);
    } finally {
      setAgentUndoBusy(false);
    }
  }, [agentUndo, agentUndoBusy, client, openSceneFromServer, projectId, resetPreview]);

  useEffect(() => {
    if (!agentUndo || agentUndoBusy) return undefined;

    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.defaultPrevented) return;
      if (isCanvasEventTarget(event.target)) return;
      if (!isAgentUndoShortcutKey(event) || isEditableEventTarget(event.target)) return;
      event.preventDefault();
      void onUndoAgentRun();
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [agentUndo, agentUndoBusy, onUndoAgentRun]);

  const activeViewLabel = useMemo(() => {
    const entry =
      activeView === "studio" ? APP_S.viewStudio
      : activeView === "editor" && editorMode === "code" ? APP_S.viewCode
      : activeView === "canvas" ? APP_S.viewCanvas
      : activeView === "assets" ? APP_S.viewAssets
      : activeView === "preview" ? APP_S.viewPreview
      : APP_S.viewEditor;
    return translate(entry, locale);
  }, [activeView, editorMode, locale]);

  const activeSelectedLine = activeView === "studio"
    ? studioSelectedLine
    : activeView === "canvas"
      ? canvasSelectedLine
      : null;

  // ── agent generation ──
  const runAgentPrompt = useCallback(
    async (prompt: string, displayPrompt = prompt, mode?: "create" | "adapt") => {
      const confirmedPrompt = prompt.trim();
      const visiblePrompt = displayPrompt.trim() || confirmedPrompt;
      const needsActiveSource = Boolean(activeEpisodeId);
      if (
        !confirmedPrompt ||
        !projectId ||
        state.status === "running" ||
        agentSubmittingRef.current ||
        (needsActiveSource && !editorSourceReady)
      ) {
        return;
      }
      agentSubmittingRef.current = true;
      setAgentSubmitting(true);
      try {
        if (writingRef.current) {
          await writingRef.current.prepareSource();
        } else if (!(await saveActiveSceneNow())) return;
        const agentValidation = activeEpisodeId ? await validateSceneForAgent(activeEpisodeId) : canvasValidation;
        const snapshots = await snapshotProjectScenes(projectId);
        const beforeIds = new Set(snapshots.map((s) => s.id));
        const toolArgs = new Map<string, Record<string, unknown>>();
        const writtenSceneIds: string[] = [];
        let wrotePlan = false;
        const startView = activeView;
        const sourceExcerpt = numberedSourceExcerpt(editorSource);
        const context: AgentRunContext = {
          activeEpisodeId,
          activeView,
          ...(sourceExcerpt ? { sourceExcerpt } : {}),
          ...selectedContextForAgent(editorSource, activeSelectedLine),
          diagnostics: diagnosticsForAgent(diagnostics, agentValidation),
        };
        setInput("");
        setAgentUndo(null);
        setAgentResult(null);
        setState((s) => startUserTurn(s, visiblePrompt));
        await client.runAgent(projectId, confirmedPrompt, (event) => {
          if (event.type === "tool.end" && event.name === "write_plan" && event.ok) wrotePlan = true;
          if (event.type === "tool.start" && event.args) {
            toolArgs.set(event.toolCallId, event.args);
          } else if (event.type === "tool.end" && event.name === "write_scene" && event.ok) {
            const sceneId = toolArgs.get(event.toolCallId)?.sceneId;
            if (typeof sceneId === "string") writtenSceneIds.push(sceneId);
          } else if (event.type === "confirmation.request") {
            const brief = agentConfirmationBriefFromRequest(event.request);
            setPendingAdaptRun(null);
            setPendingAgentRun({ prompt: visiblePrompt, brief, source: "agent", mode });
          }
          setState((s) => reduceAgentEvent(s, event));
        }, context, mode);
        if (wrotePlan) setProjectPlanRevision((revision) => revision + 1);
        const scenes = await client.listScenes(projectId);
        setEpisodes(scenes.map((s) => ({ id: s.id })));
        const sceneIds = scenes.map((s) => s.id);
        const { createdSceneIds, changedSceneIds } = await resolveAgentSceneChanges(
          client,
          projectId,
          snapshots,
          sceneIds,
          writtenSceneIds,
        );
        const changedSet = new Set(changedSceneIds);
        if (changedSceneIds.length > 0) {
          setAgentUndo({ snapshots, createdSceneIds, activeEpisodeId: activeEpisodeId ?? null });
        }
        const target =
          [...writtenSceneIds].reverse().find((id) => changedSet.has(id)) ??
          changedSceneIds.find((id) => !beforeIds.has(id)) ??
          activeEpisodeId;
        if (changedSceneIds.length > 0) {
          const validation = await validateAgentChangedScenes(
            client,
            projectId,
            changedSceneIds,
            capabilities.compiler,
          );
          setAgentResult({
            changedSceneIds,
            targetSceneId: target ?? null,
            validationChecked: validation.checked,
            validationIssues: validation.issues,
          });
        }
        if (target) {
          await openSceneFromServer(target);
          setActiveView(startView);
        } else {
          resetPreview();
        }
      } catch (err) {
        reportError(setState, err);
      } finally {
        agentSubmittingRef.current = false;
        setAgentSubmitting(false);
        void refreshAccountUsage();
      }
    },
    [
      activeEpisodeId,
      activeView,
      canvasValidation,
      activeSelectedLine,
      client,
      capabilities.compiler,
      diagnostics,
      editorSource,
      editorSourceReady,
      openSceneFromServer,
      projectId,
      refreshAccountUsage,
      resetPreview,
      saveActiveSceneNow,
      snapshotProjectScenes,
      state.status,
      validateSceneForAgent,
    ],
  );

  const requestAgentRun = useCallback(
    async (override?: string) => {
      const prompt = (override ?? input).trim();
      if (!prompt || !projectId || state.status === "running" || agentSubmittingRef.current) return;
      const needsActiveSource = Boolean(activeEpisodeId);
      if (needsActiveSource && !editorSourceReady) return;

      const kind = agentConfirmationKindForRequest({ activeEpisodeId, sourceReady: editorSourceReady, prompt });
      const brief = buildAgentConfirmationBrief({
        kind,
        locale,
        prompt,
        activeEpisodeId,
        activeViewLabel,
        selectedLine: activeSelectedLine,
        diagnosticsCount: confirmationIssueCount(diagnostics, canvasValidation),
      });
      setPendingAdaptRun(null);
      if (!shouldUseLocalPreflight({ kind, prompt })) {
        setPendingAgentRun(null);
        void runAgentPrompt(prompt, prompt, kind === "adapt" ? "adapt" : undefined);
        return;
      }
      if (!(await enterWorkspaceForLocalPreflight())) return;
      setPendingAgentRun({ prompt, brief, source: "local", mode: kind === "adapt" ? "adapt" : undefined });
    },
    [
      activeEpisodeId,
      activeViewLabel,
      activeSelectedLine,
      canvasValidation,
      diagnostics,
      enterWorkspaceForLocalPreflight,
      editorSourceReady,
      input,
      locale,
      projectId,
      runAgentPrompt,
      state.status,
    ],
  );

  const onConfirmPendingAgentRun = useCallback((answers?: readonly AgentConfirmationAnswer[]) => {
    if (!pendingAgentRun) return;
    const run = pendingAgentRun;
    const note = input.trim();
    setPendingAgentRun(null);
    if (run.source === "agent") {
      setInput("");
      void runAgentPrompt(
        confirmedAgentCheckpointInstruction({ brief: run.brief, originalPrompt: run.prompt, userNote: note, answers }),
        run.displayPrompt ?? confirmedCheckpointDisplayPrompt(run.brief, localeRef.current, answers),
        run.mode,
      );
      return;
    }
    const localNote = note && note !== run.prompt.trim() ? note : undefined;
    void runAgentPrompt(
      run.instruction ??
        confirmedAgentInstruction(run.brief.kind, run.prompt, { brief: run.brief, answers, userNote: localNote }),
      run.displayPrompt ?? run.prompt,
      run.mode,
    );
  }, [input, pendingAgentRun, runAgentPrompt]);

  const onRevisePendingRun = useCallback(() => {
    setPendingAgentRun(null);
    setPendingAdaptRun(null);
  }, []);

  // load the example episode (no AI) — opt-in from onboarding
  const onLoadExample = useCallback(async () => {
    if (!projectId) return;
    try {
      await client.writeScene(projectId, "ep1", DEMO_EPISODE);
      const scenes = await client.listScenes(projectId);
      setEpisodes(scenes.map((s) => ({ id: s.id })));
    } catch (err) {
      reportError(setState, err);
    }
  }, [client, projectId]);

  // create a fresh empty episode
  const onNewEpisode = useCallback(async () => {
    if (!projectId) return;
    const n = episodes.length + 1;
    const id = `ep${n}`;
    const stub = [
      `@episode new:${String(n).padStart(2, "0")} "New episode" {`,
      "  NARRATOR: (Start your story here…)",
      "  @gate { @end to_be_continued }",
      "}",
    ].join("\n");
    try {
      await client.writeScene(projectId, id, stub);
      const scenes = await client.listScenes(projectId);
      setEpisodes(scenes.map((s) => ({ id: s.id })));
      selectEpisode(id);
      setActiveView("writing");
    } catch (err) {
      reportError(setState, err);
    }
  }, [client, projectId, episodes.length, selectEpisode]);

  const onEditorSourceChange = useCallback((source: string) => {
    if (!activeEpisodeId || editorSourceEpisodeId !== activeEpisodeId) return;
    setCanvasValidation({
      status: capabilities.compiler && (activeView === "studio" || activeView === "canvas") ? "checking" : capabilities.compiler ? "idle" : "unavailable",
    });
    setEditorSource((previous) => {
      if (previous !== source) clearCanvasUndo();
      return source;
    });
  }, [activeEpisodeId, activeView, capabilities.compiler, clearCanvasUndo, editorSourceEpisodeId]);

  const onWritingSourceChange = useCallback((source: string) => {
    onEditorSourceChange(source);
    savedSourceRef.current = source;
    setAllScenes((prev) => prev.map((scene) => scene.id === activeEpisodeId ? { ...scene, source } : scene));
    resetPreview("source");
  }, [activeEpisodeId, onEditorSourceChange, resetPreview]);

  const pushCanvasUndo = useCallback((source: string) => {
    canvasUndoStackRef.current = [...canvasUndoStackRef.current.slice(-49), source];
    setCanvasUndoDepth(canvasUndoStackRef.current.length);
  }, []);

  const onCanvasSourceChange = useCallback((source: string) => {
    if (!activeEpisodeId || editorSourceEpisodeId !== activeEpisodeId) return;
    setCanvasValidation({ status: capabilities.compiler ? "checking" : "unavailable" });
    setEditorSource((previous) => {
      if (previous === source) return previous;
      pushCanvasUndo(previous);
      return source;
    });
    resetPreview("source");
  }, [activeEpisodeId, capabilities.compiler, editorSourceEpisodeId, pushCanvasUndo, resetPreview]);

  const onCanvasInsertBlock = useCallback((kind: InsertKind, afterLine?: number | null) => {
    if (!activeEpisodeId || editorSourceEpisodeId !== activeEpisodeId) return;
    setCanvasValidation({ status: capabilities.compiler ? "checking" : "unavailable" });
    setEditorSource((source) => {
      const result = insertCanvasBlockNearLineWithResult(source, parseLsBlocks(source), kind, afterLine);
      setCanvasSelectedLine(result.insertedLine);
      if (result.source !== source) pushCanvasUndo(source);
      return result.source;
    });
    resetPreview("source");
  }, [activeEpisodeId, capabilities.compiler, editorSourceEpisodeId, pushCanvasUndo, resetPreview]);

  const onCanvasUndoSourceChange = useCallback(() => {
    if (!activeEpisodeId || editorSourceEpisodeId !== activeEpisodeId) return;
    const restored = canvasUndoStackRef.current.at(-1);
    if (restored === undefined) return;
    canvasUndoStackRef.current = canvasUndoStackRef.current.slice(0, -1);
    setCanvasUndoDepth(canvasUndoStackRef.current.length);
    setCanvasValidation({ status: capabilities.compiler ? "idle" : "unavailable" });
    setEditorSource(restored);
    setCanvasSelectedLine(null);
    resetPreview("source");
  }, [activeEpisodeId, capabilities.compiler, editorSourceEpisodeId, resetPreview]);

  useEffect(() => {
    clearCanvasUndo();
  }, [activeEpisodeId, clearCanvasUndo]);

  const onCreateCanvasScene = useCallback(
    async (branchKey: string) => {
      if (!projectId) return;
      const id = storageIdFromBranchKey(branchKey, episodes.map((ep) => ep.id));
      const content = sceneStubForBranchKey(branchKey);
      try {
        await client.writeScene(projectId, id, content);
        const scenes = await client.listScenes(projectId);
        setEpisodes(scenes.map((s) => ({ id: s.id })));
        await openSceneFromServer(id);
        setActiveView("canvas");
      } catch (err) {
        reportError(setState, err);
      }
    },
    [client, episodes, openSceneFromServer, projectId],
  );

  // reset all per-book state, then load a book's scenes (shared by switch/new)
  const enterBook = useCallback(
    (id: string, name: string) => {
      persistSelectedProject(id);
      setProjectId(id);
      setProjectName(name);
      setActiveEpisodeId(undefined);
      setEditorSource("");
      setEditorSourceEpisodeId(null);
      savedSourceRef.current = "";
      setMapping(emptyMapping());
      setAgentUndo(null);
      setAgentResult(null);
      setPendingAgentRun(null);
      setPendingAdaptRun(null);
      setAgentUndoBusy(false);
      setState(initialAppState);
      resetPreview();
      setActiveView("writing");
    },
    [resetPreview],
  );

  // switch to another existing book
  const switchBook = useCallback(
    async (id: string) => {
      const p = projects.find((x) => x.id === id);
      if (!p || id === projectId) return;
      enterBook(id, p.name);
      try {
        const scenes = await client.listScenes(id);
        setEpisodes(scenes.map((s) => ({ id: s.id })));
      } catch (err) {
        reportError(setState, err);
      }
    },
    [projects, projectId, client, enterBook],
  );

  // start a brand-new book → empty → onboarding (Phase A) shows for it
  const startNewBook = useCallback(async () => {
    try {
      const name = translate(APP_S.bookName, localeRef.current, { n: projects.length + 1 });
      const p = await client.createProject(name);
      setProjects((prev) => [...prev, p]);
      enterBook(p.id, p.name);
      setEpisodes([]);
    } catch (err) {
      reportError(setState, err);
    }
  }, [client, projects.length, enterBook]);

  // adapt an uploaded novel into the current book's first episode (streams like the agent)
  const runAdaptNovel = useCallback(
    async (novelText: string, confirmationInstruction?: string, displayPrompt?: string) => {
      if (!novelText.trim() || !projectId || state.status === "running" || agentSubmittingRef.current) return;
      const visiblePrompt = displayPrompt?.trim() || translate(APP_S.adaptTurn, localeRef.current);
      agentSubmittingRef.current = true;
      setAgentSubmitting(true);
      try {
        if (!(await saveActiveSceneNow())) return;
        const snapshots = await snapshotProjectScenes(projectId);
        const beforeIds = new Set(snapshots.map((s) => s.id));
        const toolArgs = new Map<string, Record<string, unknown>>();
        const writtenSceneIds: string[] = [];
        let adaptationSucceeded = false;
        const startView = activeView;
        setAgentUndo(null);
        setAgentResult(null);
        setState((s) => startUserTurn(s, visiblePrompt));
        await client.runAdapt(projectId, novelText, (event) => {
          if (event.type === "done") adaptationSucceeded = true;
          if (event.type === "tool.start" && event.args) {
            toolArgs.set(event.toolCallId, event.args);
          } else if (event.type === "tool.end" && event.name === "write_scene" && event.ok) {
            const sceneId = toolArgs.get(event.toolCallId)?.sceneId;
            if (typeof sceneId === "string") writtenSceneIds.push(sceneId);
          } else if (event.type === "confirmation.request") {
            const brief = agentConfirmationBriefFromRequest(event.request);
            setPendingAgentRun(null);
            setPendingAdaptRun({ novelText, brief, source: "agent" });
          }
          setState((s) => reduceAgentEvent(s, event));
        }, undefined, confirmationInstruction);
        if (adaptationSucceeded) setProjectPlanRevision((revision) => revision + 1);
        const scenes = await client.listScenes(projectId);
        setEpisodes(scenes.map((s) => ({ id: s.id })));
        const sceneIds = scenes.map((s) => s.id);
        const { createdSceneIds, changedSceneIds } = await resolveAgentSceneChanges(
          client,
          projectId,
          snapshots,
          sceneIds,
          writtenSceneIds,
        );
        const changedSet = new Set(changedSceneIds);
        if (changedSceneIds.length > 0) {
          setAgentUndo({ snapshots, createdSceneIds, activeEpisodeId: activeEpisodeId ?? null });
        }
        const target =
          [...writtenSceneIds].reverse().find((id) => changedSet.has(id)) ??
          changedSceneIds.find((id) => !beforeIds.has(id)) ??
          activeEpisodeId;
        if (changedSceneIds.length > 0) {
          const validation = await validateAgentChangedScenes(
            client,
            projectId,
            changedSceneIds,
            capabilities.compiler,
          );
          setAgentResult({
            changedSceneIds,
            targetSceneId: target ?? null,
            validationChecked: validation.checked,
            validationIssues: validation.issues,
          });
        }
        if (target) {
          await openSceneFromServer(target);
          setActiveView(startView);
        } else {
          resetPreview();
        }
      } catch (err) {
        reportError(setState, err);
      } finally {
        agentSubmittingRef.current = false;
        setAgentSubmitting(false);
        void refreshAccountUsage();
      }
    },
    [
      activeEpisodeId,
      activeView,
      capabilities.compiler,
      client,
      openSceneFromServer,
      projectId,
      refreshAccountUsage,
      resetPreview,
      saveActiveSceneNow,
      snapshotProjectScenes,
      state.status,
    ],
  );

  const requestAdaptNovel = useCallback(
    async (novelText: string, fileName?: string) => {
      if (!novelText.trim() || !projectId || state.status === "running" || agentSubmittingRef.current) return;
      const brief = buildAgentConfirmationBrief({
        kind: "adapt",
        locale,
        prompt: translate(APP_S.adaptTurn, locale),
        fileName,
        textLength: novelText.length,
      });
      setPendingAgentRun(null);
      if (!(await enterWorkspaceForLocalPreflight())) return;
      setPendingAdaptRun({ novelText, fileName, brief, source: "local" });
    },
    [enterWorkspaceForLocalPreflight, locale, projectId, state.status],
  );

  const onConfirmPendingAdaptRun = useCallback((answers?: readonly AgentConfirmationAnswer[]) => {
    if (!pendingAdaptRun) return;
    const run = pendingAdaptRun;
    const note = input.trim();
    setPendingAdaptRun(null);
    if (run.source === "agent") {
      setInput("");
      void runAdaptNovel(
        run.novelText,
        run.confirmationInstruction ??
          confirmedAgentCheckpointInstruction({
            brief: run.brief,
            originalPrompt: translate(APP_S.adaptTurn, localeRef.current),
            userNote: note,
            answers,
          }),
        confirmedCheckpointDisplayPrompt(run.brief, localeRef.current, answers),
      );
      return;
    }
    const localNote = note && note !== translate(APP_S.adaptTurn, localeRef.current).trim() ? note : undefined;
    void runAdaptNovel(
      run.novelText,
      run.confirmationInstruction ?? confirmedAdaptInstruction(localNote, { brief: run.brief, answers }),
    );
  }, [input, pendingAdaptRun, runAdaptNovel]);

  const onNext = useCallback(() => {
    if (!controllerRef.current) return;
    controllerRef.current.next();
    syncPreview();
  }, [syncPreview]);

  const onChoose = useCallback(
    (optionId: string) => {
      if (!controllerRef.current) return;
      controllerRef.current.choose(optionId);
      syncPreview();
    },
    [syncPreview],
  );

  const onCgContinue = useCallback(() => {
    if (!controllerRef.current) return;
    controllerRef.current.cgContinue();
    syncPreview();
  }, [syncPreview]);

  const agentContextChips = useMemo<AgentContextChip[]>(() => {
    const items: AgentContextChip[] = [];
    if (activeEpisodeId) items.push({ label: translate(APP_S.agentChipEpisode, locale), value: activeEpisodeId });
    items.push({ label: translate(APP_S.agentChipView, locale), value: activeViewLabel });
    if (activeSelectedLine) {
      items.push({
        label: translate(APP_S.agentChipSelected, locale),
        value: translate(APP_S.agentChipLine, locale, { line: activeSelectedLine }),
      });
    }
    const issueCount = diagnosticsForAgent(diagnostics, canvasValidation).length;
    if (issueCount > 0) {
      items.push({
        label: translate(APP_S.agentChipDiagnostics, locale),
        value: translate(APP_S.agentChipIssueCount, locale, { count: issueCount }),
        tone: "danger",
      });
    }
    return items;
  }, [activeEpisodeId, activeSelectedLine, activeViewLabel, canvasValidation, diagnostics, locale]);

  const agentDisabledReason = useMemo(() => {
    if (token === "dev") return translate(APP_S.guestAiDisabled, locale);
    if (!projectId) return translate(APP_S.noProjectAiDisabled, locale);
    if (!activeEpisodeId) return translate(APP_S.noEpisodeAiDisabled, locale);
    if (!editorSourceReady) return translate(APP_S.sourceLoadingAiDisabled, locale);
    return null;
  }, [activeEpisodeId, editorSourceReady, locale, projectId, token]);

  const onAgentInputChange = useCallback((value: string) => {
    setInput(value);
  }, []);

  // ── render: three phases ──
  if (!token) {
    return (
      <LoginScreen
        onLogin={onLogin}
        onRegister={onRegister}
        onGuest={onGuest}
        onClearError={() => setLoginError(undefined)}
        error={loginError}
        busy={loggingIn}
      />
    );
  }

  if (!loaded) {
    return (
      <div className="lw-boot">
        <Spinner size="lg" />
      </div>
    );
  }

  if (episodes.length === 0) {
    const last = state.turns[state.turns.length - 1];
    const streamingText = last?.role === "agent" ? last.text : undefined;
    return (
      <OnboardingView
        onStartWriting={(text) => { void (async () => {
          if (!projectId) return;
          try {
            const created = await createInitialWorkspaceDraftScene(client, projectId, locale);
            const initialSource = created.source.replace(/NARRATOR:.*$/m, "NARRATOR: ");
            await client.writeScene(projectId, created.id, initialSource);
            localStorage.setItem(`lunaria-writing:${projectId}:${created.id}`, JSON.stringify({ text, source: initialSource, syncedText: "" }));
            setEpisodes([{ id: created.id }]); setActiveEpisodeId(created.id);
            setEditorSource(initialSource); setEditorSourceEpisodeId(created.id);
            savedSourceRef.current = initialSource; setAllScenes([{ id: created.id, source: initialSource }]);
            setActiveView("writing");
          } catch (err) { reportError(setState, err); }
        })(); }}
        onSend={(text) => requestAgentRun(text)}
        onAdaptNovel={(text, fileName) => requestAdaptNovel(text, fileName)}
        busy={state.status === "running"}
        streamingText={streamingText}
        starters={STARTER_PROMPTS.map((s) => translate(s, locale))}
        onStarter={(text) => requestAgentRun(text)}
        onLoadExample={onLoadExample}
        error={state.error ?? null}
        aiDisabledReason={token === "dev" ? translate(APP_S.guestAiDisabled, locale) : null}
      />
    );
  }

  return (
    <>
    <Dialog open={signInOpen} onOpenChange={setSignInOpen} title="Sign in to enable AI" description="Your story stays saved on this device." hideClose footer={<Button variant="ghost" onClick={() => setSignInOpen(false)}>Back to writing</Button>}>
      <div className="lw-reauth">
        <LoginScreen onLogin={onLogin} onRegister={onRegister} onGuest={() => setSignInOpen(false)} onClearError={() => setLoginError(undefined)} error={loginError} busy={loggingIn} />
      </div>
    </Dialog>
    <WorkspaceShell
      projectName={projectName}
      episodeId={activeEpisodeId}
      episodes={episodes}
      onSelectEpisode={selectEpisode}
      onNewEpisode={() => void onNewEpisode()}
      books={projects}
      onSwitchBook={(id) => void switchBook(id)}
      onNewBook={() => void startNewBook()}
      onDownloadProject={() => void onDownloadProject()}
      projectDownloadBusy={projectDownloadBusy}
      accountLabel={token === "dev" ? translate(COMMON.guest, locale) : (accountName ?? translate(COMMON.loggedIn, locale))}
      accountUsage={accountUsage}
      accountUsageLoading={accountUsageLoading}
      accountUsageError={accountUsageError}
      onLogout={onLogout}
      onSignIn={token === "dev" ? openSignIn : undefined}
      activeView={activeView}
      onSelectView={(view) => { void (async () => {
        try {
          if (view !== "writing") await writingRef.current?.prepareSource();
          if (view === "editor") setEditorMode("card");
          setActiveView(view);
        } catch (err) { setDiagnostics([messageOf(err)]); }
      })(); }}
      sidebarCollapsed={sidebarCollapsed}
      companionMode={companionMode}
      companionOpenKey={companionOpenKey}
      onCompanionModeChange={(mode) => { setCompanionMode(mode); if (mode === "agent") setSidebarCollapsed(false); }}
      onPreview={() => { if (activeView === "writing") void onWritingPreview(); else { setActiveView("preview"); void onCompile(); } }}
      writingSlot={editorSourceReady && activeEpisodeId && projectId ? (
        <CowritingPane key={`${projectId}:${activeEpisodeId}`} ref={writingRef}
          client={client} projectId={projectId} episodeId={activeEpisodeId}
          source={activeEditorSource} onSourceChange={onWritingSourceChange}
          aiEnabled={token !== "dev"} locale={locale}
          onPreviewHere={() => { void onWritingPreview(); }} onSignIn={openSignIn} />
      ) : <Spinner />}
      studioSlot={
        <StudioView
          value={activeEditorSource}
          onChange={onEditorSourceChange}
          assetUriMap={studioAssetUriMap}
          protagonist={projectProtagonist}
          assetMapping={mapping}
          readOnly={!editorSourceReady}
          validation={canvasValidation}
          imageGenerationEnabled={capabilities.imageGen}
          cutoutEnabled={capabilities.cutout}
          styles={styles}
          assetsBusy={assetsBusy}
          onGenerateAsset={onGenerate}
          onSuggestAssetPrompt={onSuggestPrompt}
          onPreviewAssetPrompt={onPreviewAssetPrompt}
          onCutoutAsset={onCutoutAsset}
          onSelectedLineChange={setStudioSelectedLine}
          onOpenCode={(line) => {
            setEditorInitialLine(line);
            setEditorMode("code");
            setActiveView("editor");
          }}
          onOpenAssets={() => setActiveView("assets")}
          onOpenPlaytest={() => void onOpenAgentPreview()}
        />
      }
      editorSlot={
        <EditorView
          value={activeEditorSource}
          onChange={onEditorSourceChange}
          readOnly={!editorSourceReady}
          mode={editorMode}
          onModeChange={setEditorMode}
          initialLine={editorInitialLine}
          onInitialLineApplied={() => setEditorInitialLine(null)}
          diagnostics={codeDiagnostics}
          saveState={editorSource === savedSourceRef.current ? "saved" : "unsaved"}
        />
      }
      canvasSlot={
        <CanvasView
          scenes={allScenes}
          activeId={activeEpisodeId}
          onSelect={(id) => selectEpisode(id)}
          activeSource={editorSource}
          activeSourceId={editorSourceEpisodeId}
          onActiveSourceChange={onCanvasSourceChange}
          onInsertBlock={onCanvasInsertBlock}
          onUndoSourceChange={onCanvasUndoSourceChange}
          canUndoSourceChange={canvasUndoDepth > 0}
          onCreateScene={(branchKey) => void onCreateCanvasScene(branchKey)}
          validation={canvasValidation}
          selectedLine={canvasSelectedLine}
          onSelectedLineChange={setCanvasSelectedLine}
        />
      }
      assetsSlot={
        <AssetsView
          parsed={parsed}
          mapping={mapping}
          assets={assets}
          capabilities={{ imageGen: capabilities.imageGen }}
          styles={styles}
          busy={assetsBusy}
          onUpload={onUpload}
          onBind={onBind}
          onUnbind={onUnbind}
          onGenerate={onGenerate}
          onSuggestPrompt={onSuggestPrompt}
        />
      }
      previewSlot={
        <PreviewView
          stage={stage}
          decision={decision}
          isDone={isDone}
          diagnostics={diagnostics}
          compiling={compiling}
          canCompile={capabilities.compiler && Boolean(activeEpisodeId)}
          compilerAvailable={capabilities.compiler}
          staleReason={previewInvalidReason}
          onCompile={activeView === "writing" ? onWritingPreview : onCompile}
          onNext={onNext}
          onChoose={onChoose}
          onCgContinue={onCgContinue}
          onRestart={activeView === "writing" ? onWritingPreview : onCompile}
          source={editorSource}
          episodeId={activeEpisodeId ?? undefined}
        />
      }
      sidebarSlot={
        <AgentSidebar
          state={state}
          input={input}
          onInput={onAgentInputChange}
          onSend={() => requestAgentRun()}
          collapsed={sidebarCollapsed}
          onToggleCollapse={() => setSidebarCollapsed((c) => !c)}
          disabledReason={agentDisabledReason}
          busy={agentSubmitting}
          contextChips={agentContextChips}
          undoAvailable={Boolean(agentUndo)}
          undoBusy={agentUndoBusy}
          onUndo={onUndoAgentRun}
          resultChangedSceneIds={agentResult?.changedSceneIds}
          resultValidationChecked={agentResult?.validationChecked}
          resultValidationIssues={agentResult?.validationIssues}
          pendingConfirmation={pendingAgentRun?.brief ?? pendingAdaptRun?.brief ?? null}
          onConfirmPending={pendingAdaptRun ? onConfirmPendingAdaptRun : onConfirmPendingAgentRun}
          onRevisePending={onRevisePendingRun}
          onOpenPreview={() => void onOpenAgentPreview()}
          onOpenCanvas={() => setActiveView("canvas")}
          onOpenEditor={() => {
            setEditorMode("card");
            setActiveView("editor");
          }}
        />
      }
    />
    </>
  );
}
